import React, {
  useRef,
  useEffect,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BryntumTaskBoard } from '@bryntum/taskboard-react';
import { AuthContext } from '../context/auth-context';
import { alertActions } from '../../store/alert';
import ProjectEmptyState from '../projects/ProjectEmptyState';
import WorkflowAPI from '../../services/api/workflow/workflow-api';
import {
  Box,
  CircularProgress,
  Typography,
  Snackbar,
  Alert,
  Chip,
  Button,
  Autocomplete,
  TextField,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Backdrop,
  IconButton,
  Tooltip,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import ACCAPI from '../../services/api/integrations/acc-api';
import { calculateForecastAndVariance } from '../Analytics/ProductionMetrics/components/workPackageProgressModal/forecastMath';
import '@bryntum/taskboard/taskboard.css';
import '@bryntum/taskboard/svalbard-light.css';
import '@bryntum/taskboard/fontawesome/css/fontawesome.css';
import '@bryntum/taskboard/fontawesome/css/solid.css';
import './PackageWorkflow.css';
import ProjectTeamModal from './ProjectTeamModal';
import PackageExportDialog from './PackageExport/PackageExportDialog';
import { useNotify } from '../ui';
import WorkPackageProgressModal from '../Analytics/ProductionMetrics/components/workPackageProgressModal/WorkPackageProgressModal';
import PackageHistoryModal from '../shared/PackageHistoryModal/PackageHistoryModal';
import ProductionMetricsAPI from '../../services/api/analytics/production-metrics-api';
import {
  FIELD_START_DATE_TOO_FAR_FUTURE_MESSAGE,
  getMaxFieldStartDateCalendarYmd,
  isCalendarDateBeyondMaxFieldStartAhead,
  parseCalendarYmd,
} from '../../utils/field-start-date-policy';
import {
  calendarDaysBetween,
  formatCalendarDate,
  getTodayCalendarYmd,
} from '../../utils/calendarDate';

/** Local calendar YYYY-MM-DD for comparisons and API (avoid UTC drift from toISOString). */
function formatDateAsYmdLocal(date) {
  if (date == null || date === '') return null;
  if (typeof date === 'string') {
    const part = date.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(part)) return part;
  }
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Bryntum store changes may be the new value or { value, oldValue }. */
function getStoreChangeNewValue(changeEntry) {
  if (changeEntry === undefined) return undefined;
  if (
    changeEntry !== null &&
    typeof changeEntry === 'object' &&
    !Array.isArray(changeEntry) &&
    !(changeEntry instanceof Date) &&
    Object.prototype.hasOwnProperty.call(changeEntry, 'value')
  ) {
    return changeEntry.value;
  }
  return changeEntry;
}

function fieldStartDateToYmd(value) {
  if (value === null || value === undefined || value === '') return null;
  return parseCalendarYmd(value);
}

/**
 * Kanban card urgency border class for a swim lane due date.
 * Compares calendar days (not timestamps) so it is timezone- and DST-safe.
 * @param {string|Date|null|undefined} dueDate
 * @returns {string} 'due-overdue' | 'due-at-risk' | ''
 */
function getDueUrgencyCls(dueDate) {
  const diffDays = calendarDaysBetween(getTodayCalendarYmd(), dueDate);
  if (diffDays === null) return '';
  if (diffDays < 0) return 'due-overdue'; // Red border — past due
  if (diffDays <= 3) return 'due-at-risk'; // Orange border — due within 3 days
  return '';
}

/**
 * Format a timestamp as "Mon D, YYYY" for the status-history table.
 * Not for calendar-only dates — use formatCalendarDate for those.
 */
function formatHistoryDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Escape text before injecting into status-history HTML. */
function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Build the Info-tab tracking-status-history table HTML from editorData.statusHistory.
 * Newest entry is shown last (bottom); its TO lane gets the highlighted chip.
 */
function renderStatusHistoryHtml(history) {
  const title =
    '<div class="status-history-title">Tracking Status History</div>';
  if (!Array.isArray(history) || history.length === 0) {
    return `<div class="status-history-section">${title}<p class="status-history-empty">No status history recorded yet.</p></div>`;
  }
  const ordered = [...history].reverse();
  const lastIdx = ordered.length - 1;
  const rows = ordered
    .map((h, idx) => {
      const toChipClass =
        idx === lastIdx
          ? 'status-history-lane-chip current'
          : 'status-history-lane-chip';
      const fromLane = h.oldStatusName
        ? `<span class="status-history-lane-chip">${escapeHtml(h.oldStatusName)}</span>`
        : '<span class="status-history-lane-empty">—</span>';
      const toLane = `<span class="${toChipClass}">${escapeHtml(h.newStatusName || '—')}</span>`;
      return `<tr>
        <td>${formatHistoryDate(h.changedAt)}</td>
        <td>${fromLane}</td>
        <td>${toLane}</td>
        <td>${escapeHtml(h.changedByName)}</td>
      </tr>`;
    })
    .join('');
  return `<div class="status-history-section">
    ${title}
    <table class="status-history-table">
      <thead><tr><th>Date</th><th>From</th><th>To</th><th>Changed by</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </div>`;
}

/**
 * Build the header package block. The bold title is `autodesk_name - description`;
 * the secondary line is the package area when present.
 */
function renderPackageHeader(taskRecord) {
  const description = taskRecord.description || '';
  const autodeskName = taskRecord.autodeskName || '';
  const title =
    autodeskName && description
      ? `${autodeskName} - ${description}`
      : description || autodeskName || 'Package';
  const ref = taskRecord.areaName || '';
  return `<div class="header-package-block">
    <div class="header-package-name" title="${escapeHtml(title)}">${escapeHtml(title)}</div>
    ${ref ? `<div class="header-package-ref" title="${escapeHtml(ref)}">${escapeHtml(ref)}</div>` : ''}
  </div>`;
}

/** Build the VDC / Shop / Subcontractor toggle pills from the task record. */
function renderFlagPills(taskRecord) {
  const pill = (key, label, icon) => {
    const required = taskRecord[key] === 'Required';
    return `<button type="button" class="flag-pill${required ? ' required' : ''}" data-flag="${key}">
      <i class="fa ${icon}"></i> ${label}: ${required ? 'required' : 'not required'}
    </button>`;
  };
  return `<div class="flag-pills">
    ${pill('vdcRequired', 'VDC', 'fa-drafting-compass')}
    ${pill('shopRequired', 'Shop', 'fa-cart-flatbed')}
    ${pill('subcontractorRequired', 'Sub', 'fa-users')}
  </div>`;
}

/**
 * Build the Schedule-tab Planned vs Actual date grid from editorData.dates.
 * Shop rows are shaded. Null actual finish → "In progress"; null actual start → "Not set".
 */
function renderPlannedActualHtml(dates) {
  if (!dates) return '';
  const fmt = (value) => formatCalendarDate(value) ?? '—';
  const plannedRows = [
    ['Shop start', fmt(dates.plannedShopStartDate), 'shop'],
    ['Shop finish', fmt(dates.plannedShopFinishDate), 'shop'],
    ['Field start', fmt(dates.fieldStartDate), ''],
    ['Field finish', fmt(dates.plannedFieldFinishDate), ''],
  ]
    .map(
      ([label, val, cls]) =>
        `<div class="pa-row ${cls}"><span>${label}</span><span>${val}</span></div>`
    )
    .join('');

  const actualCell = (value, kind) => {
    if (value) return `<span>${fmt(value)}</span>`;
    const text = kind === 'finish' ? 'In progress' : 'Not set';
    return `<span class="pa-val-muted">${text}</span>`;
  };
  const actualRows = [
    ['Shop start', actualCell(dates.shopActualStartDate, 'start'), 'shop'],
    ['Shop finish', actualCell(dates.shopActualFinishDate, 'finish'), 'shop'],
    ['Field start', actualCell(dates.fieldActualStartDate, 'start'), ''],
    ['Field finish', actualCell(dates.fieldActualFinishDate, 'finish'), ''],
  ]
    .map(
      ([label, cell, cls]) =>
        `<div class="pa-row ${cls}"><span>${label}</span>${cell}</div>`
    )
    .join('');

  return `<div class="planned-actual-grid">
    <div class="pa-col">
      <div class="pa-col-header">Planned</div>${plannedRows}
    </div>
    <div class="pa-col">
      <div class="pa-col-header">Actual</div>${actualRows}
    </div>
  </div>`;
}

/**
 * Format hours for earned-row metrics (matches WorkPackageProgressModal EarnedRow).
 */
function formatMetricHours(value) {
  if (value == null || !Number.isFinite(value)) return '\u2014';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function renderEarnedMetricBlock(
  label,
  value,
  { warning = false, danger = false } = {}
) {
  const formatted = formatMetricHours(value);
  const showUnit = value != null && Number.isFinite(value);
  const valCls = danger
    ? 'earned-metric-val earned-metric-val-danger'
    : warning
      ? 'earned-metric-val earned-metric-val-warning'
      : 'earned-metric-val';
  return `<div class="earned-metric">
    <span class="earned-metric-lbl">${escapeHtml(label)}</span>
    <span class="${valCls}">${formatted}${
      showUnit ? '<span class="earned-metric-unit">hrs</span>' : ''
    }</span>
  </div>`;
}

/**
 * Build a Hours-tab stat card (Shop or Field) from editorData.hours values.
 * Burned % = actual/budget; shown in danger color when over 100%.
 * Earned row matches WorkPackageProgressModal EarnedRow layout.
 */
function renderHoursCardHtml(label, data, complete) {
  const budget = Number(data?.budget) || 0;
  const actual = Number(data?.actual) || 0;
  const pct =
    data?.percentComplete != null
      ? Math.round(Number(data.percentComplete))
      : 0;
  const storedEarned = Number(data?.earnedHrs);
  const earnedHrs = Number.isFinite(storedEarned)
    ? storedEarned
    : budget * (pct / 100) || 0;
  const { forecastHrs, leftToCompleteHrs, budgetVarianceHrs } =
    calculateForecastAndVariance(budget, actual, pct);
  const leftWarning = leftToCompleteHrs != null && leftToCompleteHrs < 0;
  const varianceDanger = budgetVarianceHrs != null && budgetVarianceHrs < 0;
  const burned = budget > 0 ? (actual / budget) * 100 : 0;
  const burnedCls = burned > 100 ? 'stat-val burned-over' : 'stat-val';
  const completeChip = complete
    ? '<span class="hours-complete-chip">complete</span>'
    : '';
  return `<div class="hours-card">
    <div class="hours-card-header">${escapeHtml(label)}${completeChip}</div>
    <div class="hours-stat-row">
      <div class="hours-stat"><span class="stat-lbl">Budget</span><span class="stat-val">${budget} hrs</span></div>
      <div class="hours-stat"><span class="stat-lbl">Actual</span><span class="stat-val">${actual} hrs</span></div>
      <div class="hours-stat"><span class="stat-lbl">Burned</span><span class="${burnedCls}">${burned.toFixed(1)}%</span></div>
    </div>
    <div class="hours-earned-row">
      <div class="earned-pct-col">
        <span class="earned-pct-val">${pct}</span><span class="earned-pct-symbol">%</span>
      </div>
      <div class="earned-metrics-col">
        ${renderEarnedMetricBlock('Earned Hrs', earnedHrs)}
        ${renderEarnedMetricBlock('Forecast Hrs', forecastHrs)}
        ${renderEarnedMetricBlock('Left to Complete', leftToCompleteHrs, {
          warning: leftWarning,
        })}
        ${renderEarnedMetricBlock('Budget Variance', budgetVarianceHrs, {
          danger: varianceDanger,
        })}
      </div>
    </div>
  </div>`;
}

/** Whether the open package requires shop fabrication (Hours tab shop card). */
function taskRequiresShopFabrication(taskRecord) {
  if (!taskRecord) return false;
  if (taskRecord.requiresShopFabrication != null) {
    return !!taskRecord.requiresShopFabrication;
  }
  return taskRecord.shopRequired === 'Required';
}

/** Paint the Hours-tab shop + field stat cards from an editorData payload. */
function populateHoursCards(editor, editorData, taskRecord) {
  if (!editor || !editorData) return;
  const h = editorData.hours || {};
  const dts = editorData.dates || {};
  const requiresShop = taskRequiresShopFabrication(taskRecord);
  const shopCard = editor.widgetMap?.shopHoursCard;
  if (shopCard) {
    shopCard.hidden = !requiresShop;
    if (requiresShop) {
      shopCard.html = renderHoursCardHtml(
        'Shop',
        {
          budget: h.shopBudgetHrs,
          actual: h.shopActualHrs,
          earnedHrs: h.shopEarnedHrs,
          percentComplete: h.shopPercentComplete,
        },
        !!dts.shopActualFinishDate
      );
    } else {
      shopCard.html = '';
    }
  }
  const fieldCard = editor.widgetMap?.fieldHoursCard;
  if (fieldCard) {
    fieldCard.html = renderHoursCardHtml(
      'Field',
      {
        budget: h.fieldBudgetHrs,
        actual: h.fieldActualHrs,
        earnedHrs: h.fieldEarnedHrs,
        percentComplete: h.fieldPercentComplete,
      },
      !!dts.fieldActualFinishDate
    );
  }
}

/** Find a package row in production-metrics categories (any bucket). */
function findPackageInProgressCategories(categories, packageId) {
  if (!categories?.length || !packageId) return null;
  for (const cat of categories) {
    const found = (cat.packages || []).find((p) => p.id === packageId);
    if (found) return found;
  }
  return null;
}

/**
 * Merge production-metrics package row with workflow editor-data so
 * WorkPackageProgressModal gets the same shape as on the metrics dashboard.
 */
function buildProgressModalPackage(apiPkg, editorData, taskRecord) {
  const h = editorData?.hours || {};
  const dts = editorData?.dates || {};
  const base = apiPkg || {};
  return {
    id: base.id || taskRecord?.id,
    name: base.name || taskRecord?.description || '',
    autodeskName: base.autodeskName ?? taskRecord?.autodeskName ?? null,
    requiresShop:
      base.requiresShop != null
        ? !!base.requiresShop
        : !!taskRecord?.requiresShopFabrication,
    fieldBudgetHrs:
      base.fieldBudgetHrs ?? base.fieldHrsBudgeted ?? h.fieldBudgetHrs ?? 0,
    shopBudgetHrs:
      base.shopBudgetHrs ?? base.shopHrsBudgeted ?? h.shopBudgetHrs ?? 0,
    fieldActualHrs: base.fieldActualHrs ?? h.fieldActualHrs ?? 0,
    shopActualHrs: base.shopActualHrs ?? h.shopActualHrs ?? 0,
    fieldPercentComplete:
      base.fieldPercentComplete ?? h.fieldPercentComplete ?? 0,
    shopPercentComplete: base.shopPercentComplete ?? h.shopPercentComplete ?? 0,
    fieldPctUpdatedAt: base.fieldPctUpdatedAt ?? null,
    fieldPctUpdatedBy: base.fieldPctUpdatedBy ?? null,
    shopPctUpdatedAt: base.shopPctUpdatedAt ?? null,
    shopPctUpdatedBy: base.shopPctUpdatedBy ?? null,
    schedStart: base.schedStart ?? dts.fieldStartDate ?? null,
    schedEnd: base.schedEnd ?? dts.plannedFieldFinishDate ?? null,
    shopSchedStart: base.shopSchedStart ?? dts.plannedShopStartDate ?? null,
    shopSchedEnd: base.shopSchedEnd ?? dts.plannedShopFinishDate ?? null,
    fieldActualStartDate:
      base.fieldActualStartDate ?? dts.fieldActualStartDate ?? null,
    fieldActualFinishDate:
      base.fieldActualFinishDate ?? dts.fieldActualFinishDate ?? null,
    shopActualStartDate:
      base.shopActualStartDate ?? dts.shopActualStartDate ?? null,
    shopActualFinishDate:
      base.shopActualFinishDate ?? dts.shopActualFinishDate ?? null,
  };
}

/** Close the Bryntum package editor popup before opening a React overlay. */
function closeBryntumTaskEditor(board) {
  const taskEdit = board?.features?.taskEdit;
  const editor = taskEdit?.editor;
  if (editor?.close) {
    editor.close();
    return;
  }
  if (typeof taskEdit?.cancel === 'function') {
    taskEdit.cancel();
  }
}

/** Reopen the package editor for a task after the progress modal closes. */
function reopenBryntumPackageEditor(board, packageId) {
  if (!board || packageId == null) return false;
  const taskStore = board.project?.taskStore;
  const taskRecord =
    (typeof taskStore?.getById === 'function' &&
      taskStore.getById(packageId)) ||
    taskStore?.allRecords?.find((t) => String(t.id) === String(packageId));
  if (!taskRecord) return false;
  if (typeof board.editTask === 'function') {
    board.editTask(taskRecord);
    return true;
  }
  const taskEdit = board.features?.taskEdit;
  if (taskEdit && typeof taskEdit.edit === 'function') {
    taskEdit.edit(taskRecord);
    return true;
  }
  return false;
}

const PackageWorkflow = () => {
  const isSmallScreen = useMediaQuery('(max-width:1970px)');
  const workflowBoardRef = useRef(null);
  const isUpdatingFromApiRef = useRef(false); // Flag to prevent listener from saving during API-driven updates
  const isUpdatingStatusRef = useRef(false); // Flag to suppress autosave during status drag/drop updates
  const scheduleRefreshRef = useRef(null); // Used to refresh swim lane schedule when field start date changes in editor
  const editingPmRef = useRef(null); // Tracks the PM value when editor opens (for change detection)
  const editingStatusRef = useRef(null); // Tracks the status when editor opens (for change detection on close)
  /** Last accepted fieldStartDate per open editor (revert target on validation failure). */
  const fieldStartDateSnapshotRef = useRef({ taskId: null, value: null });
  const isRevertingFieldStartDateRef = useRef(false);
  /** Consolidated /editor-data result for the currently open editor (hours, dates, history, ACC activity). */
  const editorDataRef = useRef(null);

  const fieldStartDateErrorShownAtRef = useRef(0);
  const showFieldStartDateTooFarError = useCallback((editor) => {
    const fsdField = editor?.widgetMap?.fieldStartDate;
    fsdField?.setError?.(FIELD_START_DATE_TOO_FAR_FUTURE_MESSAGE);
    const now = Date.now();
    if (now - fieldStartDateErrorShownAtRef.current < 500) return;
    fieldStartDateErrorShownAtRef.current = now;
    setSnackbar({
      open: true,
      message: FIELD_START_DATE_TOO_FAR_FUTURE_MESSAGE,
      severity: 'error',
    });
  }, []);

  const revertFieldStartDateToSnapshot = useCallback(
    (record, taskId, editor) => {
      const snap = fieldStartDateSnapshotRef.current;
      const revertValue = snap.taskId === taskId ? snap.value : null;
      isRevertingFieldStartDateRef.current = true;
      try {
        if (typeof record?.set === 'function') {
          record.set('fieldStartDate', revertValue, true);
        } else if (record) {
          record.fieldStartDate = revertValue;
        }
        const fsdField = editor?.widgetMap?.fieldStartDate;
        if (fsdField) {
          fsdField.value = revertValue;
        }
      } finally {
        isRevertingFieldStartDateRef.current = false;
      }
    },
    []
  );
  const auth = useContext(AuthContext);
  const dispatch = useDispatch();
  const isSuperAdmin = !!auth.isSuperAdmin;

  // Global project selection from Redux (set via the sidebar drawer).
  const selectedProject = useSelector((state) => state.project.selectedProject);
  const selectedProjectId = selectedProject?.id || '';

  // State for project selection
  const [isLoadingKanban, setIsLoadingKanban] = useState(false);
  const [kanbanData, setKanbanData] = useState(null);
  const [error, setError] = useState(null);

  // State for status step sets (workflows)
  const [statusStepSets, setStatusStepSets] = useState([]);
  const [selectedStatusStepSetId, setSelectedStatusStepSetId] = useState(null);
  const [selectedStatusStepSet, setSelectedStatusStepSet] = useState(null);
  const [isSwitchingWorkflow, setIsSwitchingWorkflow] = useState(false);

  // State for Project Team modal
  const [projectTeamModalOpen, setProjectTeamModalOpen] = useState(false);

  // Package status download dialog (#245)
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const notify = useNotify();

  // Counter to force Bryntum TaskBoard remount when data refreshes
  const [boardRefreshKey, setBoardRefreshKey] = useState(0);

  // Filter and toggle state
  const [selectedPmFilter, setSelectedPmFilter] = useState(null);
  const [selectedResourceFilter, setSelectedResourceFilter] = useState(null);
  const [vdcFilter, setVdcFilter] = useState(false);
  const [prefabFilter, setPrefabFilter] = useState(false);
  const [groupByPm, setGroupByPm] = useState(false); // Used in Phase 3
  const [groupByResource, setGroupByResource] = useState(false);
  const [filteredCount, setFilteredCount] = useState(0);

  // State for status updates
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  /** Super admin past-date warning for Field Installation Start Date (workflow modal). */
  const [fieldStartPastWarning, setFieldStartPastWarning] = useState(false);
  const [autodeskSyncWarningOpen, setAutodeskSyncWarningOpen] = useState(false);
  // When true, the sync failed specifically because the Autodesk token expired.
  const [autodeskSyncAuthExpired, setAutodeskSyncAuthExpired] = useState(false);

  // State for the Update Work Package Progress modal launched from the Hours tab
  const [wpProgressModalOpen, setWpProgressModalOpen] = useState(false);
  const [wpProgressPackage, setWpProgressPackage] = useState(null);
  const [wpProgressSaving, setWpProgressSaving] = useState(false);
  const [wpProgressLoading, setWpProgressLoading] = useState(false);
  const [wpProgressError, setWpProgressError] = useState(null);
  const openWpProgressFromHoursTabRef = useRef(null);
  /** Package id to restore in Bryntum editor after the progress modal closes. */
  const reopenEditorPackageIdRef = useRef(null);

  // State for the shared Package History modal launched from the Hours tab
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyModalPackage, setHistoryModalPackage] = useState(null);
  const [historyModalDefaultTab, setHistoryModalDefaultTab] =
    useState('status');

  // Force hide body scrollbars to prevent double scrollbars
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    // Cleanup: restore body scroll on unmount
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Fetch kanban data when project is selected
  useEffect(() => {
    const fetchKanbanData = async () => {
      if (!selectedProjectId) {
        setKanbanData(null);
        setStatusStepSets([]);
        setSelectedStatusStepSetId(null);
        return;
      }

      try {
        setIsLoadingKanban(true);
        setError(null);

        const data = await WorkflowAPI.getKanbanBoardData(
          selectedProjectId,
          auth.token
        );
        setKanbanData(data);
        setFilteredCount(data.packages?.length || 0);

        // Set status step sets and selected one from API response
        setStatusStepSets(data.statusStepSets || []);
        setSelectedStatusStepSetId(data.selectedStatusStepSetId || null);
        // Store the selected workflow object for the modal
        if (data.selectedStatusStepSetId && data.statusStepSets) {
          const selectedSet = data.statusStepSets.find(
            (s) => s.id === data.selectedStatusStepSetId
          );
          setSelectedStatusStepSet(selectedSet || null);
        }
      } catch (err) {
        console.error('Kanban API Error:', err.response?.data || err.message);
        setError('Failed to load kanban data');
        setKanbanData(null);
        setStatusStepSets([]);
        setSelectedStatusStepSetId(null);
      } finally {
        setIsLoadingKanban(false);
      }
    };

    if (auth.token && selectedProjectId) {
      fetchKanbanData();
    }
  }, [auth.token, selectedProjectId]);

  // Handle status step set (workflow) change
  const handleStatusStepSetChange = useCallback(
    async (stepSetId) => {
      if (stepSetId === selectedStatusStepSetId) return; // Already selected

      try {
        setIsLoadingKanban(true);
        setIsSwitchingWorkflow(true);
        setError(null);

        const data = await WorkflowAPI.getKanbanBoardData(
          selectedProjectId,
          auth.token,
          stepSetId
        );

        setKanbanData(data);
        setSelectedStatusStepSetId(data.selectedStatusStepSetId || stepSetId);
        // Store the selected workflow object for the modal
        const selectedSet = statusStepSets.find((s) => s.id === stepSetId);
        setSelectedStatusStepSet(selectedSet || null);
      } catch (err) {
        console.error('Error loading workflow data:', err);
        setError('Failed to load workflow data');
      } finally {
        setIsLoadingKanban(false);
        setIsSwitchingWorkflow(false);
      }
    },
    [selectedProjectId, selectedStatusStepSetId, auth.token]
  );

  // Transform statuses to Bryntum columns
  const columns = useMemo(() => {
    if (!kanbanData?.statuses?.length) {
      return [];
    }

    return kanbanData.statuses.map((status) => ({
      id: status.id,
      text: status.label || status.name,
      color: status.color || 'blue',
      laneType: status.swim_lane_type || 'standard',
      swimLaneType: status.swim_lane_type || 'standard',
      minWidth: 300, // Increased width for better readability
    }));
  }, [kanbanData?.statuses]);

  // Get resources (team members) from kanbanData, sorted alphabetically
  const resources = useMemo(() => {
    if (!kanbanData?.resources?.length) {
      return [];
    }
    return kanbanData.resources
      .map((resource) => ({
        id: resource.id,
        name: resource.name,
        // Bryntum uses these for displaying initials when no image is provided
        eventColor: '#3B82F6', // Blue color for avatar background
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [kanbanData?.resources]);

  // Derive project team member list for PM combo and PM filter Autocomplete
  const projectTeamMembers = useMemo(() => {
    if (!kanbanData?.resources?.length) return [];
    return kanbanData.resources
      .map((r) => ({ id: r.id, name: r.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [kanbanData?.resources]);

  // PM filter options: "Unassigned" sentinel + team members (filter-only, never used by the PM combo)
  const pmFilterOptions = useMemo(
    () => [{ id: 'unassigned', name: 'Unassigned' }, ...projectTeamMembers],
    [projectTeamMembers]
  );

  // Employees assigned to at least one package on the current board (for resource filter)
  const boardResourceMembers = useMemo(() => {
    const list = kanbanData?.board_resources;
    if (!list?.length) return [];
    return list
      .map((r) => ({
        id: r.id,
        name: r.display_name ?? r.displayName ?? '',
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [kanbanData?.board_resources]);

  // Transform packages to Bryntum tasks
  const tasks = useMemo(() => {
    if (!kanbanData?.packages?.length) {
      return [];
    }

    return kanbanData.packages.map((pkg) => {
      // Build card title: [Area Name] - [autodesk_name], omitting whichever part
      // is missing. Local (non-ACC) packages have no autodesk_name, so this shows
      // just the area name instead of "Area - null".
      const cardTitle =
        pkg.area_name && pkg.autodesk_name
          ? `${pkg.area_name} - ${pkg.autodesk_name}`
          : pkg.area_name || pkg.autodesk_name || '';

      // Calculate urgency class based on due date
      let urgencyCls = getDueUrgencyCls(pkg.swim_lane_due_date);

      if (pkg.archived) {
        urgencyCls += (urgencyCls ? ' ' : '') + 'package-card-archived';
      }

      return {
        id: pkg.id,
        name: cardTitle, // Card title (combined format)
        cls: urgencyCls, // Urgency border class (applied by Bryntum to card element)
        archived: pkg.archived || false,
        areaName: pkg.area_name || '', // Separate field for modal
        autodeskName: pkg.autodesk_name || '', // Separate field for modal
        status: pkg.current_tracking_status_id, // Links to column by status ID
        description: pkg.name || 'Unnamed Package',
        // Custom fields for swim lane tracking
        // Append T12:00:00 so date-only strings are not parsed as UTC midnight (which shows as previous day in timezones behind UTC, e.g. Azure)
        fieldStartDate: pkg.field_start_date
          ? new Date(String(pkg.field_start_date).split('T')[0] + 'T12:00:00')
          : null,
        dueDate: pkg.swim_lane_due_date
          ? new Date(String(pkg.swim_lane_due_date).split('T')[0] + 'T12:00:00')
          : null,
        notes: pkg.swim_lane_notes || '',
        // ACC custom attribute fields for read-only display
        zone: pkg.zone || '',
        programArea: pkg.program_area || '',
        workTypeCode: pkg.work_type_code || '',
        workTypeName: pkg.work_type_name || '',
        iwpId: pkg.iwp_id || '',
        twpCode: pkg.twp_code || '',
        trade: pkg.trade_name || '',
        fieldStartDateLocked: pkg.field_start_date_locked || false,
        fieldStartDateSource: pkg.field_start_date_source || 'manual',
        // Installation duration (working days). pg numeric arrives as a string.
        duration: pkg.duration != null ? Number(pkg.duration) : null,
        subcontractorRequired: pkg.subcontractor_required
          ? 'Required'
          : 'Not Required',
        vdcRequired: pkg.requires_vdc_design ? 'Required' : 'Not Required',
        shopRequired: pkg.requires_shop_fabrication
          ? 'Required'
          : 'Not Required',
        // PM assignment fields
        pmEmployeeId: pkg.pm_employee_id || null,
        pmDisplayName: pkg.pm_display_name || null,
        // swimlanePmId is used in Phase 3 but mapped now to avoid revisiting
        swimlanePmId: pkg.pm_employee_id || 'unassigned',
        // Package resource owners (package_assignments), ordered by assigned_at on the server
        resourceIds: Array.isArray(pkg.resource_ids) ? pkg.resource_ids : [],
        primaryResourceId: pkg.primary_resource_id || null,
        primaryResourceName: pkg.primary_resource_name || null,
        swimlaneResourceId: pkg.primary_resource_id || 'unassigned',
        // Boolean versions for filter logic (Phase 1E/2)
        requiresVdcDesign: !!pkg.requires_vdc_design,
        requiresShopFabrication: !!pkg.requires_shop_fabrication,
        // ACC deep link fields
        autodeskAssetId: pkg.autodesk_asset_id || null,
        autodeskProjectId: pkg.autodesk_project_id || null,
        autodeskLastSynced: pkg.autodesk_last_synced || null,
      };
    });
  }, [kanbanData?.packages]);

  // Create assignments array for Bryntum (links tasks to resources)
  const assignments = useMemo(() => {
    if (!kanbanData?.assignments?.length) {
      return [];
    }
    return kanbanData.assignments.map((a) => ({
      id: a.id,
      event: a.event, // taskId (package id)
      resource: a.resource, // resourceId (employee id)
    }));
  }, [kanbanData?.assignments]);

  // Handle snackbar close
  const handleSnackbarClose = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  // Central filter function — manages all named filters together
  // Uses removeFilter('id') instead of clearFilters() to preserve Bryntum's built-in search filters
  const applyBoardFilters = useCallback(
    (pmFilter, resourceFilter, vdc, prefab) => {
      const taskBoard = workflowBoardRef.current?.instance;
      const taskStore = taskBoard?.project?.taskStore;
      if (!taskStore) return;

      // Remove only the filters owned by this feature — never clearFilters()
      taskStore.removeFilter('pmFilter');
      taskStore.removeFilter('resourceFilter');
      taskStore.removeFilter('vdcFilter');
      taskStore.removeFilter('prefabFilter');

      if (pmFilter) {
        taskStore.addFilter({
          id: 'pmFilter',
          filterBy: (task) =>
            pmFilter.id === 'unassigned'
              ? !task.pmEmployeeId
              : task.pmEmployeeId === pmFilter.id,
        });
      }

      if (resourceFilter) {
        taskStore.addFilter({
          id: 'resourceFilter',
          filterBy: (task) =>
            (task.resourceIds || []).includes(resourceFilter.id),
        });
      }

      if (vdc) {
        taskStore.addFilter({
          id: 'vdcFilter',
          filterBy: (task) => task.requiresVdcDesign === true,
        });
      }

      if (prefab) {
        taskStore.addFilter({
          id: 'prefabFilter',
          filterBy: (task) => task.requiresShopFabrication === true,
        });
      }

      // Update filtered count after applying filters
      setFilteredCount(taskStore.count);
    },
    []
  );

  // PM filter change handler — passes full filter state to central function
  const handlePmFilterChange = useCallback(
    (newValue) => {
      setSelectedPmFilter(newValue);
      applyBoardFilters(
        newValue,
        selectedResourceFilter,
        vdcFilter,
        prefabFilter
      );
    },
    [applyBoardFilters, selectedResourceFilter, vdcFilter, prefabFilter]
  );

  const handleResourceFilterChange = useCallback(
    (newValue) => {
      setSelectedResourceFilter(newValue);
      applyBoardFilters(selectedPmFilter, newValue, vdcFilter, prefabFilter);
    },
    [applyBoardFilters, selectedPmFilter, vdcFilter, prefabFilter]
  );

  // VDC filter toggle handler
  const handleVdcFilterChange = useCallback(() => {
    const newValue = !vdcFilter;
    setVdcFilter(newValue);
    applyBoardFilters(
      selectedPmFilter,
      selectedResourceFilter,
      newValue,
      prefabFilter
    );
  }, [
    applyBoardFilters,
    selectedPmFilter,
    selectedResourceFilter,
    vdcFilter,
    prefabFilter,
  ]);

  // Prefab filter toggle handler
  const handlePrefabFilterChange = useCallback(() => {
    const newValue = !prefabFilter;
    setPrefabFilter(newValue);
    applyBoardFilters(
      selectedPmFilter,
      selectedResourceFilter,
      vdcFilter,
      newValue
    );
  }, [
    applyBoardFilters,
    selectedPmFilter,
    selectedResourceFilter,
    vdcFilter,
    prefabFilter,
  ]);

  // Convenience boolean for toolbar display
  const anyFilterActive =
    !!selectedPmFilter || !!selectedResourceFilter || vdcFilter || prefabFilter;

  // Swimlane definitions derived from the live Bryntum task store on toggle
  const [pmSwimlanesData, setPmSwimlanesData] = useState(null);
  const [resourceSwimlanesData, setResourceSwimlanesData] = useState(null);

  // Reset all filter/toggle state when the global project changes.
  useEffect(() => {
    setSelectedPmFilter(null);
    setSelectedResourceFilter(null);
    setVdcFilter(false);
    setPrefabFilter(false);
    setGroupByPm(false);
    setGroupByResource(false);
    setPmSwimlanesData(null);
    setResourceSwimlanesData(null);
    setFilteredCount(0);
  }, [selectedProjectId]);

  // Toggle handler for Group-by-PM swimlane view
  const handleGroupByPmToggle = useCallback(() => {
    const newValue = !groupByPm;
    setGroupByPm(newValue);

    if (newValue) {
      if (groupByResource) {
        setGroupByResource(false);
        setResourceSwimlanesData(null);
      }

      // Derive lanes from the live Bryntum task store so in-session PM edits are captured
      const taskBoard = workflowBoardRef.current?.instance;
      const taskStore = taskBoard?.project?.taskStore;
      const pmMap = new Map();

      // Build a lookup from projectTeamMembers for display names
      const teamNameLookup = new Map(
        projectTeamMembers.map((m) => [m.id, m.name])
      );

      if (taskStore) {
        taskStore.allRecords.forEach((task) => {
          if (task.pmEmployeeId && !pmMap.has(task.pmEmployeeId)) {
            const displayName =
              task.pmDisplayName ||
              teamNameLookup.get(task.pmEmployeeId) ||
              task.pmEmployeeId;
            pmMap.set(task.pmEmployeeId, displayName);
          }
        });
      }

      const lanes = Array.from(pmMap.entries()).map(([id, displayName]) => ({
        id,
        text: displayName,
      }));
      lanes.push({ id: 'unassigned', text: 'Unassigned' });
      setPmSwimlanesData(lanes);

      if (selectedPmFilter) {
        setSelectedPmFilter(null);
        applyBoardFilters(
          null,
          selectedResourceFilter,
          vdcFilter,
          prefabFilter
        );
      }
    } else {
      setPmSwimlanesData(null);
    }
  }, [
    groupByPm,
    groupByResource,
    selectedPmFilter,
    selectedResourceFilter,
    applyBoardFilters,
    vdcFilter,
    prefabFilter,
    projectTeamMembers,
  ]);

  const handleGroupByResourceToggle = useCallback(() => {
    const newValue = !groupByResource;
    setGroupByResource(newValue);

    if (newValue) {
      if (groupByPm) {
        setGroupByPm(false);
        setPmSwimlanesData(null);
      }

      const taskBoard = workflowBoardRef.current?.instance;
      const taskStore = taskBoard?.project?.taskStore;
      const resourceMap = new Map();
      const nameLookup = new Map(
        boardResourceMembers.map((m) => [m.id, m.name])
      );

      if (taskStore) {
        taskStore.allRecords.forEach((task) => {
          const rid = task.primaryResourceId;
          if (rid && !resourceMap.has(rid)) {
            const displayName =
              task.primaryResourceName || nameLookup.get(rid) || rid;
            resourceMap.set(rid, displayName);
          }
        });
      }

      const lanes = Array.from(resourceMap.entries()).map(([id, text]) => ({
        id,
        text,
      }));
      lanes.push({ id: 'unassigned', text: 'Unassigned' });
      setResourceSwimlanesData(lanes);

      if (selectedResourceFilter) {
        setSelectedResourceFilter(null);
        applyBoardFilters(selectedPmFilter, null, vdcFilter, prefabFilter);
      }
    } else {
      setResourceSwimlanesData(null);
    }
  }, [
    groupByResource,
    groupByPm,
    selectedResourceFilter,
    selectedPmFilter,
    applyBoardFilters,
    vdcFilter,
    prefabFilter,
    boardResourceMembers,
  ]);

  // Handle Project Team modal close - only refresh if specifically needed
  const handleCloseProjectTeamModal = useCallback(() => {
    setProjectTeamModalOpen(false);
  }, []);

  // ── Update Work Package Progress modal (launched from the Hours tab) ──────
  // Closes the Bryntum editor first, then opens the same modal as Production
  // Metrics with a full package row from GET /production-metrics/packages.

  const reopenEditorAfterWpProgress = useCallback(async () => {
    const packageId = reopenEditorPackageIdRef.current;
    reopenEditorPackageIdRef.current = null;
    if (packageId == null) return;

    await new Promise((resolve) => setTimeout(resolve, 50));

    for (let attempt = 0; attempt < 15; attempt++) {
      const board = workflowBoardRef.current?.instance;
      if (reopenBryntumPackageEditor(board, packageId)) return;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    console.warn('Could not reopen package editor for package', packageId);
  }, []);

  const handleCloseWpProgress = useCallback(() => {
    if (wpProgressSaving) return;
    setWpProgressModalOpen(false);
    setWpProgressPackage(null);
    setWpProgressError(null);
    void reopenEditorAfterWpProgress();
  }, [wpProgressSaving, reopenEditorAfterWpProgress]);

  const openWpProgressFromHoursTab = useCallback(
    async (taskRecord) => {
      if (!taskRecord?.id || !selectedProjectId || wpProgressLoading) return;

      const board = workflowBoardRef.current?.instance;
      const editorData = editorDataRef.current;
      const packageId = taskRecord.id;

      closeBryntumTaskEditor(board);
      // Let Bryntum finish tearing down the popup before the MUI dialog opens.
      await new Promise((resolve) => setTimeout(resolve, 50));

      setWpProgressLoading(true);
      setWpProgressError(null);
      try {
        const result = await ProductionMetricsAPI.getPackages(
          selectedProjectId,
          auth.token
        );
        const apiPkg = findPackageInProgressCategories(
          result?.categories,
          packageId
        );
        const pkg = buildProgressModalPackage(apiPkg, editorData, taskRecord);
        if (!pkg?.id) {
          throw new Error('Could not load package progress data');
        }
        reopenEditorPackageIdRef.current = packageId;
        setWpProgressPackage(pkg);
        setWpProgressModalOpen(true);
      } catch (err) {
        console.error('Error opening work package progress modal:', err);
        setSnackbar({
          open: true,
          message:
            err?.response?.data?.message ||
            err.message ||
            'Failed to load package progress data',
          severity: 'error',
        });
      } finally {
        setWpProgressLoading(false);
      }
    },
    [auth.token, selectedProjectId, wpProgressLoading]
  );

  useEffect(() => {
    openWpProgressFromHoursTabRef.current = openWpProgressFromHoursTab;
  }, [openWpProgressFromHoursTab]);

  // Refresh the Kanban board data - called when swim lane assignments are made
  // Uses direct store updates for smooth UX (no remount, preserves filters/scroll)
  const refreshBoardData = useCallback(async () => {
    if (!selectedProjectId || !selectedStatusStepSetId) return;

    try {
      console.log('refreshBoardData: fetching data...');
      const data = await WorkflowAPI.getKanbanBoardData(
        selectedProjectId,
        auth.token,
        selectedStatusStepSetId
      );

      console.log('refreshBoardData: got data', {
        resourceCount: data.resources?.length,
        assignmentCount: data.assignments?.length,
        sampleAssignments: data.assignments?.slice(0, 5),
      });

      // Update React state
      setKanbanData(data);

      // Directly update Bryntum stores for smooth UX (no full remount)
      const board = workflowBoardRef.current?.instance;
      if (board?.project) {
        const { resourceStore, assignmentStore } = board.project;

        console.log('refreshBoardData: updating Bryntum stores');

        // Set flag AND suspend assignment events BEFORE any store operations
        // This prevents the listener from firing during:
        // 1. Resource removal (which cascades to assignment removal)
        // 2. Direct assignment updates
        isUpdatingFromApiRef.current = true;
        if (assignmentStore) {
          assignmentStore.suspendEvents();
        }

        try {
          // Update resources (team members) - add new ones, keep existing
          if (resourceStore && data.resources) {
            const newResources = data.resources.map((r) => ({
              id: r.id,
              name: r.name,
              eventColor: '#3B82F6',
            }));

            // Find resources that don't exist yet and add them
            const existingIds = new Set(
              resourceStore.allRecords.map((r) => r.id)
            );
            const toAdd = newResources.filter((r) => !existingIds.has(r.id));
            if (toAdd.length > 0) {
              console.log('refreshBoardData: adding resources', toAdd);
              resourceStore.add(toAdd);
            }

            // Remove resources that are no longer in the team
            // Cascade deletes will be queued but won't trigger listener (events suspended)
            const newIds = new Set(newResources.map((r) => r.id));
            const toRemove = resourceStore.allRecords.filter(
              (r) => !newIds.has(r.id)
            );
            if (toRemove.length > 0) {
              console.log(
                'refreshBoardData: removing resources',
                toRemove.map((r) => r.name)
              );
              resourceStore.remove(toRemove);
            }
          }

          // Update assignments
          if (assignmentStore && data.assignments) {
            console.log('refreshBoardData: updating assignments', {
              currentCount: assignmentStore.count,
              newCount: data.assignments.length,
            });
            // Clear and reload all assignments
            assignmentStore.removeAll();
            const newAssignments = data.assignments.map((a) => ({
              id: a.id,
              eventId: a.event,
              resourceId: a.resource,
            }));
            assignmentStore.add(newAssignments);
            console.log(
              'refreshBoardData: assignments updated, new count:',
              assignmentStore.count
            );
          }
        } finally {
          // Resume events - any queued events will fire but listener will see flag is true
          if (assignmentStore) {
            assignmentStore.resumeEvents();
          }
          // Clear flag after a delay to ensure all queued events are processed
          setTimeout(() => {
            isUpdatingFromApiRef.current = false;
          }, 200);
        }
      } else {
        console.log(
          'refreshBoardData: no board instance, falling back to full remount'
        );
        // Fallback: force remount if stores not available
        setBoardRefreshKey((prev) => prev + 1);
      }
    } catch (err) {
      console.error('Error refreshing kanban data:', err);
    }
  }, [selectedProjectId, selectedStatusStepSetId, auth.token]);

  const validateLaneEligibilityDrop = useCallback((event) => {
    const task =
      event?.taskRecord ||
      event?.taskRecords?.[0] ||
      event?.record ||
      event?.task;
    const targetColumn = event?.targetColumn || event?.column;
    const laneType =
      targetColumn?.laneType ||
      targetColumn?.swimLaneType ||
      targetColumn?.data?.laneType ||
      targetColumn?.data?.swimLaneType;

    if (!task || !laneType) return true;

    const description = task.description || '';
    const autodeskName = task.autodeskName || '';
    const taskName =
      autodeskName && description
        ? `${autodeskName} - ${description}`
        : description || autodeskName || 'This package';
    if (laneType === 'prefab' && task.requiresShopFabrication !== true) {
      setSnackbar({
        open: true,
        message: `${taskName} is not set to require shop fabrication. Enable "Requires Shop Fabrication" before moving it into Prefab.`,
        severity: 'warning',
        autoHideDuration: 6000,
      });
      return false;
    }

    if (laneType === 'vdc' && task.requiresVdcDesign !== true) {
      setSnackbar({
        open: true,
        message: `${taskName} is not set to require VDC design. Enable "Requires VDC Design" before moving it into VDC.`,
        severity: 'warning',
        autoHideDuration: 6000,
      });
      return false;
    }

    return true;
  }, []);

  const handleWpProgressSave = useCallback(
    async (
      pkgId,
      {
        fieldPercentComplete,
        shopPercentComplete,
        notes,
        saveFieldPercent,
        saveShopPercent,
      }
    ) => {
      setWpProgressSaving(true);
      setWpProgressError(null);
      try {
        const patch = {};

        if (saveFieldPercent) {
          const pctResult = await ProductionMetricsAPI.updatePercentComplete(
            pkgId,
            {
              pctType: 'field',
              percentComplete: fieldPercentComplete,
              source: 'workflow_board',
              notes,
            },
            auth.token
          );
          patch.fieldPercentComplete = pctResult.fieldPercentComplete;
          patch.fieldPctUpdatedAt = pctResult.fieldPctUpdatedAt;
          patch.fieldPctUpdatedBy = pctResult.updatedBy;
          patch.shopPercentComplete = pctResult.shopPercentComplete;
          patch.shopPctUpdatedAt = pctResult.shopPctUpdatedAt;
        }

        if (saveShopPercent) {
          const pctResult = await ProductionMetricsAPI.updatePercentComplete(
            pkgId,
            {
              pctType: 'shop',
              percentComplete: shopPercentComplete,
              source: 'workflow_board',
              notes,
            },
            auth.token
          );
          patch.shopPercentComplete = pctResult.shopPercentComplete;
          patch.shopPctUpdatedAt = pctResult.shopPctUpdatedAt;
          patch.shopPctUpdatedBy = pctResult.updatedBy;
          patch.fieldPercentComplete = pctResult.fieldPercentComplete;
          patch.fieldPctUpdatedAt = pctResult.fieldPctUpdatedAt;
        }

        setWpProgressPackage((prev) => (prev ? { ...prev, ...patch } : prev));

        setWpProgressModalOpen(false);
        setWpProgressPackage(null);
        setSnackbar({
          open: true,
          message: 'Work package progress saved',
          severity: 'success',
        });
        await refreshBoardData();
        await reopenEditorAfterWpProgress();
      } catch (err) {
        setWpProgressError(
          err?.response?.data?.message || err.message || 'Save failed'
        );
      } finally {
        setWpProgressSaving(false);
      }
    },
    [auth.token, refreshBoardData, reopenEditorAfterWpProgress]
  );

  const handleWpProgressBudgetConfirm = useCallback(
    async (pkgId, { newShopBudgetHrs, newFieldBudgetHrs, notes }) => {
      setWpProgressSaving(true);
      setWpProgressError(null);
      try {
        const budgetResult = await ProductionMetricsAPI.updateBudgetSplit(
          pkgId,
          { newShopBudgetHrs, newFieldBudgetHrs, notes },
          auth.token
        );
        const patch = {
          shopBudgetHrs: budgetResult.shopBudgetHrs,
          fieldBudgetHrs: budgetResult.fieldBudgetHrs,
        };
        setWpProgressPackage((prev) => (prev ? { ...prev, ...patch } : prev));
        await refreshBoardData();
      } catch (err) {
        setWpProgressError(
          err?.response?.data?.message || err.message || 'Save failed'
        );
        throw err;
      } finally {
        setWpProgressSaving(false);
      }
    },
    [auth.token, refreshBoardData]
  );

  // Handle task drop - update package tracking status
  // Shared status-change pipeline used by both drag-and-drop and the editor's
  // Status dropdown. Persists via WorkflowAPI.updatePackageTrackingStatus
  // (DB write + package_status_history audit + auto-assignment + ACC sync +
  // swim_lane_due_date recalc), then mirrors the result into the board: the
  // record's dueDate/urgency cls, the assignment store, and a sync-aware
  // snackbar. `source` ('drag' | 'editor') only changes how we revert on error.
  const applyPackageStatusChange = useCallback(
    async ({ record, newStatusId, source }) => {
      // Alias kept so the body below reads the same as the original drag handler
      const task = record;
      const packageId = record.id;

      // Find the original status from our kanbanData
      const originalPackage = kanbanData?.packages?.find(
        (p) => p.id === packageId
      );
      const oldStatusId = originalPackage?.current_tracking_status_id;

      // Nothing changed → no update needed
      if (!newStatusId || oldStatusId === newStatusId) {
        return;
      }

      if (isUpdatingStatusRef.current) {
        setSnackbar({
          open: true,
          message: 'Please wait for the current update to complete',
          severity: 'warning',
        });
        return;
      }

      try {
        isUpdatingStatusRef.current = true;
        setIsUpdatingStatus(true);

        // Call API to update the status
        const updatedPackage = await WorkflowAPI.updatePackageTrackingStatus(
          packageId,
          newStatusId,
          auth.token
        );

        // Update local kanbanData for consistency (e.g., when switching projects)
        // but do it in a way that doesn't trigger Bryntum to re-initialize
        // We mutate the existing package object instead of creating new arrays
        if (originalPackage) {
          originalPackage.current_tracking_status_id = newStatusId;
          originalPackage.status_name = updatedPackage.status_name;
          originalPackage.archived = updatedPackage.archived;
        }

        // Update the Bryntum task record's archived property
        task.archived = updatedPackage.archived;

        // Update the Bryntum task record's due date with the recalculated value
        // Append T12:00:00 to avoid timezone-shift off-by-one (UTC midnight → local prev day)
        let newCls = '';
        if (updatedPackage?.swim_lane_due_date) {
          const safeDateStr =
            String(updatedPackage.swim_lane_due_date).split('T')[0] +
            'T12:00:00';
          task.dueDate = new Date(safeDateStr);
          // Recalculate urgency border class
          newCls = getDueUrgencyCls(updatedPackage.swim_lane_due_date);
        } else {
          task.dueDate = null;
        }

        if (updatedPackage.archived) {
          newCls += (newCls ? ' ' : '') + 'package-card-archived';
        }
        task.cls = newCls;

        // Update Bryntum's assignmentStore with new assignments from API
        // Use suspendEvents to prevent the listener from trying to save these changes
        const assignmentsUpdated = { removed: 0, added: 0 };
        if (workflowBoardRef.current?.instance) {
          const project = workflowBoardRef.current.instance.project;
          if (project?.assignmentStore) {
            // Set flag AND suspend events to prevent listener from saving
            isUpdatingFromApiRef.current = true;

            // Suspend events on the store to prevent listeners from firing
            project.assignmentStore.suspendEvents();

            try {
              // Remove existing assignments for this package
              const existingAssignments = project.assignmentStore.query(
                (record) =>
                  record.event === packageId || record.eventId === packageId
              );
              if (existingAssignments.length > 0) {
                project.assignmentStore.remove(existingAssignments);
                assignmentsUpdated.removed = existingAssignments.length;
              }

              // Add new assignments from API response (if any)
              if (
                updatedPackage.assignments &&
                updatedPackage.assignments.length > 0
              ) {
                const newAssignmentRecords = updatedPackage.assignments.map(
                  (a) => ({
                    eventId: a.packageId,
                    resourceId: a.employeeId,
                  })
                );
                project.assignmentStore.add(newAssignmentRecords);
                assignmentsUpdated.added = newAssignmentRecords.length;
              }
            } finally {
              // Resume events after update is complete
              project.assignmentStore.resumeEvents();

              // Clear flag after a short delay
              setTimeout(() => {
                isUpdatingFromApiRef.current = false;
              }, 100);
            }
          }
        }

        // Show success message with Autodesk sync status and assignment info
        const autodeskSync = updatedPackage.autodeskSync;
        let message = `Package "${originalPackage?.name || 'Unknown'}" moved to ${updatedPackage.status_name || 'new status'}`;

        // Add assignment info to message
        if (assignmentsUpdated.added > 0) {
          const assigneeNames = updatedPackage.assignments
            ?.map((a) => a.resourceName)
            .filter(Boolean)
            .join(', ');
          message += ` → Assigned to: ${assigneeNames || `${assignmentsUpdated.added} team member(s)`}`;
        } else if (
          assignmentsUpdated.removed > 0 &&
          assignmentsUpdated.added === 0
        ) {
          message += ' → Assignments cleared (no swim lane owner)';
        }

        const outboundBlocked =
          autodeskSync?.blocked === true ||
          (typeof autodeskSync?.error === 'string' &&
            autodeskSync.error
              .toLowerCase()
              .includes('overridden to not sync to autodesk'));

        if (autodeskSync?.synced && !outboundBlocked && !autodeskSync?.error) {
          message = `✓ ${message} and synced to Autodesk`;
        } else if (outboundBlocked) {
          message = `${message} (Autodesk sync intentionally skipped in this environment)`;
        } else if (
          autodeskSync?.error &&
          autodeskSync.error !== 'Missing Autodesk IDs'
        ) {
          message = `${message} (Autodesk sync failed: ${autodeskSync.error})`;
        }

        setSnackbar({
          open: true,
          message,
          severity:
            (autodeskSync?.synced &&
              !outboundBlocked &&
              !autodeskSync?.error) ||
            outboundBlocked ||
            !autodeskSync?.error ||
            autodeskSync?.error === 'Missing Autodesk IDs'
              ? 'success'
              : 'warning',
        });
      } catch (err) {
        console.error('Error updating package status:', err);

        if (source === 'editor') {
          // Editor path: snap the card back by reverting the record's status.
          // This fires a status-only taskStore update, which the update listener
          // ignores; isUpdatingStatusRef is also still set here, so it won't
          // re-trigger a save.
          task.status = oldStatusId;
        } else {
          // Drag path: the Bryntum board already moved the card visually, so
          // reload the board to revert it.
          const data = await WorkflowAPI.getKanbanBoardData(
            selectedProjectId,
            auth.token
          );
          setKanbanData(data);
        }

        // Extract error details from response
        const errorResponse = err.response?.data;
        const errorMessage =
          errorResponse?.message || 'Failed to update package status';
        const errorType = errorResponse?.error?.type;
        const requiresReauth = errorResponse?.error?.requiresReauth;
        const statusCode = err.response?.status;

        // Check if this is an Autodesk auth error that requires re-authentication
        const isAuthError =
          requiresReauth ||
          statusCode === 401 ||
          errorType === 'AutodeskAuthError' ||
          errorMessage.toLowerCase().includes('sign in') ||
          errorMessage.toLowerCase().includes('session') ||
          errorMessage.toLowerCase().includes('autodesk') ||
          errorMessage.toLowerCase().includes('permission');

        dispatch(
          alertActions.showAlert({
            title: isAuthError
              ? 'Error updating package status: Autodesk Authentication Required'
              : 'There was an error updating the package status',
            message: isAuthError
              ? "Your Autodesk session has expired or you don't have permission. Please sign in to Autodesk again."
              : errorMessage,
          })
        );
      } finally {
        isUpdatingStatusRef.current = false;
        setIsUpdatingStatus(false);
      }
    },
    [kanbanData?.packages, auth.token, selectedProjectId]
  );

  // Always-current pointer to applyPackageStatusChange. The taskStore `update`
  // listener (inside the workflowBoardProps memo) is bound when the board mounts,
  // so calling the callback directly there could capture a stale kanbanData
  // closure. Going through this ref guarantees the instant-save uses the latest
  // version. (The drag path doesn't need this — onTaskDrop is re-applied on every
  // render by the React wrapper.)
  const applyPackageStatusChangeRef = useRef(applyPackageStatusChange);
  applyPackageStatusChangeRef.current = applyPackageStatusChange;

  // Drag-and-drop handler: derive the new status from the drop event and
  // delegate to the shared status-change pipeline.
  const handleTaskDrop = useCallback(
    async (event) => {
      // Bryntum TaskBoard passes tasks in taskRecords array
      const taskRecords = event.taskRecords;
      const targetColumn = event.targetColumn;

      if (!taskRecords || taskRecords.length === 0) {
        console.error('No taskRecords found in event:', event);
        return;
      }

      // Get the first task (we handle single task drops)
      const task = taskRecords[0];
      // The new status is from the target column's id
      const newStatusId = targetColumn?.id || task.status;

      await applyPackageStatusChange({
        record: task,
        newStatusId,
        source: 'drag',
      });
    },
    [applyPackageStatusChange]
  );

  // Handler for when the task editor closes (the editor popup's `hide` event).
  // Persists a Status dropdown change the same way drag-and-drop does — DB write
  // + package_status_history audit + auto-assignment + ACC sync + swim_lane_due_
  // date recalc. Every other editable field (notes, PM, requirements, field
  // start date) autosaves live via the taskStore `update` listener, so status is
  // the only thing left to handle on close. The editor uses autoUpdateRecord, so
  // taskRecord.status already holds the chosen value here.
  const handleTaskEditHide = useCallback(
    async ({ taskRecord }) => {
      scheduleRefreshRef.current = null; // Clear so we don't refresh a closed editor
      // Past-date board warning persists after close; cleared on next editor open
      // when handleBeforeTaskEditShow does not re-trigger it.
      if (!taskRecord?.id) return;

      const statusChanged =
        taskRecord.status && taskRecord.status !== editingStatusRef.current;
      if (statusChanged) {
        await applyPackageStatusChange({
          record: taskRecord,
          newStatusId: taskRecord.status,
          source: 'editor',
        });
      }
    },
    [applyPackageStatusChange]
  );

  // Handler for when task editor opens - load swim lane schedule
  const handleBeforeTaskEditShow = useCallback(
    async ({ editor, taskRecord }) => {
      if (!taskRecord?.id) return;

      setFieldStartPastWarning(false);

      fieldStartDateSnapshotRef.current = {
        taskId: taskRecord.id,
        value: taskRecord.fieldStartDate ?? null,
      };

      // Capture PM value at editor open for change detection on close
      editingPmRef.current = taskRecord.pmEmployeeId ?? null;

      // The Status combo lives inside a nested container (headerStrip). A ColumnCombo
      // there does not auto-populate its column store, so the selected value renders
      // with no text. Re-link it to the board, rebuild the chained column store, and
      // re-apply the value so the display label resolves.
      const statusCombo = editor?.widgetMap?.column;
      const boardInstance = workflowBoardRef.current?.instance;
      if (statusCombo && boardInstance?.columns) {
        try {
          statusCombo._taskBoard = boardInstance;
          if (typeof boardInstance.columns.chain === 'function') {
            statusCombo.store = boardInstance.columns.chain(null);
          }
          const currentColumn = taskRecord.status;
          if (currentColumn != null) {
            statusCombo.value = currentColumn;
          }
        } catch (e) {
          console.warn('Could not relink status combo store', e);
        }
      }

      // Header package block (name + ref)
      const packageInfoWidget = editor?.widgetMap?.packageInfo;
      if (packageInfoWidget) {
        packageInfoWidget.html = renderPackageHeader(taskRecord);
      }

      // VDC / Shop / Subcontractor pills: render from the record and wire a toggle
      // handler. Setting the record field triggers the existing autosave listener.
      const flagPillsWidget = editor?.widgetMap?.flagPills;
      if (flagPillsWidget) {
        flagPillsWidget.html = renderFlagPills(taskRecord);
        const pillsEl = flagPillsWidget.element || flagPillsWidget.el;
        if (pillsEl && !pillsEl._flagHandlerAttached) {
          pillsEl._flagHandlerAttached = true;
          pillsEl.addEventListener('click', (e) => {
            const btn = e.target.closest('.flag-pill');
            if (!btn) return;
            const flag = btn.dataset.flag;
            const next =
              taskRecord[flag] === 'Required' ? 'Not Required' : 'Required';
            if (typeof taskRecord.set === 'function') {
              taskRecord.set(flag, next);
            } else {
              taskRecord[flag] = next;
            }
            if (flag === 'shopRequired') {
              taskRecord.requiresShopFabrication = next === 'Required';
              populateHoursCards(editor, editorDataRef.current, taskRecord);
            }
            flagPillsWidget.html = renderFlagPills(taskRecord);
          });
        }
      }

      // Load consolidated editor data (hours, dates, status history, ACC activity).
      // Stored in a ref so the Bryntum handler can read it without React re-renders.
      // Populates the Info-tab status-history table; Hours/Schedule tabs consume it later.
      editorDataRef.current = null;
      const historyWidget = editor?.widgetMap?.trackingStatusHistory;
      if (historyWidget) {
        historyWidget.html =
          '<div class="status-history-loading">Loading status history…</div>';
      }
      try {
        const editorData = await WorkflowAPI.getPackageEditorData(
          taskRecord.id,
          auth.token
        );
        editorDataRef.current = editorData;
        if (historyWidget) {
          historyWidget.html = renderStatusHistoryHtml(
            editorData?.statusHistory
          );
        }

        // Schedule tab: planned vs actual grid
        const plannedActualWidget = editor?.widgetMap?.plannedActualDates;
        if (plannedActualWidget) {
          plannedActualWidget.html = renderPlannedActualHtml(editorData?.dates);
        }

        // Hours tab: shop + field stat cards
        populateHoursCards(editor, editorData, taskRecord);

        // Hours tab: wire the "Update work package progress" button — closes the
        // Bryntum editor, fetches full package row from production metrics, then
        // opens WorkPackageProgressModal (see openWpProgressFromHoursTab).
        const updateBtnWidget = editor?.widgetMap?.updateProgressBtnWidget;
        const updateBtnEl = updateBtnWidget?.element || updateBtnWidget?.el;
        if (updateBtnEl && !updateBtnEl._wpHandlerAttached) {
          updateBtnEl._wpHandlerAttached = true;
          updateBtnEl.addEventListener('click', (e) => {
            if (!e.target.closest('.update-progress-btn')) return;
            e.preventDefault();
            e.stopPropagation();
            const board = workflowBoardRef.current?.instance;
            const ed = board?.features?.taskEdit?.editor;
            const record = ed?.record ?? ed?.activeRecord ?? taskRecord;
            if (!record?.id) return;
            openWpProgressFromHoursTabRef.current?.(record);
          });
        }
      } catch (err) {
        console.error('Error loading editor data:', err);
        if (historyWidget) {
          historyWidget.html =
            '<div class="status-history-error">Failed to load status history.</div>';
        }
      }

      // Hours tab: wire the history buttons to open the shared
      // PackageHistoryModal (DOM delegation, same _*Attached flag pattern as the
      // update-progress button). Independent of editor-data so a failed fetch
      // does not disable history. Each button carries a data-history-tab key.
      const historyBtnWidget = editor?.widgetMap?.hoursHistoryButtons;
      const historyBtnEl = historyBtnWidget?.element || historyBtnWidget?.el;
      if (historyBtnEl && !historyBtnEl._historyHandlerAttached) {
        historyBtnEl._historyHandlerAttached = true;
        historyBtnEl.addEventListener('click', (e) => {
          const btn = e.target.closest('.hist-btn-placeholder');
          if (!btn) return;
          const tabKey = btn.getAttribute('data-history-tab');
          if (!tabKey) return;
          setHistoryModalPackage({
            id: taskRecord.id,
            autoDeskName: taskRecord.autodeskName || '',
            name: taskRecord.description || '',
            area: taskRecord.areaName || '',
          });
          setHistoryModalDefaultTab(tabKey);
          setHistoryModalOpen(true);
        });
      }

      // Capture status at editor open so we can detect a dropdown change on close.
      // The close itself is handled by the board's `taskEditHide` listener (see
      // workflowBoardProps), which calls handleTaskEditHide to persist a status
      // change. autoUpdateRecord commits field changes to the record live, so
      // taskRecord holds the chosen status by the time that fires.
      editingStatusRef.current = taskRecord.status ?? null;

      // Inject ACC deep link icon into the title bar DOM (next to "Edit Package" text)
      if (editor) {
        const titleEl = editor.element?.querySelector('.b-header-title');
        if (titleEl) {
          const existingLink = titleEl.querySelector(
            '.acc-title-link, .acc-title-link-disabled'
          );
          if (existingLink) existingLink.remove();

          if (taskRecord.autodeskAssetId && taskRecord.autodeskProjectId) {
            const accUrl = `https://acc.autodesk.com/build/assets/projects/${taskRecord.autodeskProjectId}/assets?assetId=${taskRecord.autodeskAssetId}`;
            const lastSyncedText = taskRecord.autodeskLastSynced
              ? ` — Last synced: ${new Date(taskRecord.autodeskLastSynced).toLocaleString()}`
              : '';
            const link = document.createElement('a');
            link.href = accUrl;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.className = 'acc-title-link';
            link.title = `View in Autodesk Construction Cloud${lastSyncedText}`;
            link.textContent = 'View in ACC';
            titleEl.appendChild(link);
          } else {
            const span = document.createElement('span');
            span.className = 'acc-title-link-disabled';
            span.title = 'Not yet synced to ACC';
            span.innerHTML = '<i class="fa fa-external-link-alt"></i>';
            titleEl.appendChild(span);
          }
        }
      }

      // Installation duration: editable only when the package has no ACC schedule
      // link. Reset to read-only on every open (the editor is reused across cards)
      // and unlock after the link check below confirms it's unlinked.
      const durationField = editor?.widgetMap?.duration;
      const setDurationEditable = (editable, tooltip) => {
        if (!durationField) return;
        durationField.readOnly = !editable;
        durationField.element?.classList.toggle('acc-editable-field', editable);
        durationField.tooltip = tooltip;
      };
      setDurationEditable(false, 'Checking ACC schedule link…');

      // Field-start-date lock, beside the start date input. Protects only
      // field_start_date from ACC schedule sync — it has no effect on duration.
      // Rendered independently of the schedule-link fetch so it works even if that fails.
      const lockTitleFor = (isLocked) =>
        isLocked
          ? 'Locked — this will keep the field start date from being overwritten by an ACC schedule sync.'
          : 'Unlocked — an ACC schedule sync may overwrite this field start date.';
      const lockSourceTextFor = (record) =>
        `Source: ${record?.fieldStartDateSource === 'schedule_sync' ? 'ACC Schedule' : 'Manual entry'}`;
      const fieldStartLockWidget = editor?.widgetMap?.fieldStartLock;
      if (fieldStartLockWidget) {
        const locked = !!taskRecord.fieldStartDateLocked;
        fieldStartLockWidget.html = `
          <div class="field-start-lock-row">
            <button type="button" class="field-start-lock-btn" data-package-id="${taskRecord.id}" data-locked="${locked}" title="${lockTitleFor(locked)}">
              <i class="fa ${locked ? 'fa-lock' : 'fa-lock-open'}" style="color:${locked ? '#ed6c02' : '#999'}"></i>
            </button>
            <span class="field-start-source-stack">
              <span class="field-start-source">${lockSourceTextFor(taskRecord)}</span>
              <span class="field-start-locked-tag"${locked ? '' : ' hidden'}>Locked</span>
            </span>
          </div>`;

        // Event delegation, attached once per editor element (the editor is reused)
        const lockContainerEl =
          fieldStartLockWidget.element || fieldStartLockWidget.el;
        if (lockContainerEl && !lockContainerEl._lockHandlerAttached) {
          lockContainerEl._lockHandlerAttached = true;
          lockContainerEl.addEventListener('click', async (e) => {
            const lockBtn = e.target.closest('.field-start-lock-btn');
            if (!lockBtn) return;
            e.preventDefault();

            const pkgId = lockBtn.dataset.packageId;
            const newLocked = lockBtn.dataset.locked !== 'true';

            // Resolve the currently open record — this listener outlives the
            // taskRecord of the first open, so don't close over it.
            const board = workflowBoardRef.current?.instance;
            const ed = board?.features?.taskEdit?.editor;
            const record = ed?.record ?? ed?.activeRecord;

            try {
              await ACCAPI.updateFieldStartDate(
                pkgId,
                { locked: newLocked },
                auth.token
              );

              if (record && String(record.id) === String(pkgId)) {
                record.fieldStartDateLocked = newLocked;
              }

              const icon = lockBtn.querySelector('i');
              if (icon) {
                icon.className = newLocked ? 'fa fa-lock' : 'fa fa-lock-open';
                icon.style.color = newLocked ? '#ed6c02' : '#999';
              }
              lockBtn.dataset.locked = String(newLocked);
              lockBtn.title = lockTitleFor(newLocked);

              // "Locked" sits on its own line so the widget width never changes
              const lockedTagEl = lockContainerEl.querySelector(
                '.field-start-locked-tag'
              );
              if (lockedTagEl) lockedTagEl.hidden = !newLocked;

              setSnackbar({
                open: true,
                message: newLocked
                  ? 'Field start date locked — ACC schedule sync will not overwrite it'
                  : 'Field start date unlocked — ACC schedule sync may overwrite it',
                severity: 'success',
              });
            } catch (err) {
              console.error('Error toggling lock:', err);
              setSnackbar({
                open: true,
                message: 'Failed to update lock state',
                severity: 'error',
              });
            }
          });
        }
      }

      // Load and display schedule activity panel
      const scheduleActivityContainer =
        editor?.widgetMap?.scheduleActivityPanel;
      if (scheduleActivityContainer) {
        scheduleActivityContainer.html =
          '<div class="sched-act-loading">Loading schedule activity...</div>';

        const formatSchedDate = (dateStr) => {
          if (!dateStr) return '\u2014';
          const parts = String(dateStr).split('T')[0].split('-');
          if (parts.length !== 3) return '\u2014';
          return `${parts[1]}/${parts[2]}/${parts[0].slice(-2)}`;
        };

        try {
          const schedData = await ACCAPI.getPackageScheduleLink(
            taskRecord.id,
            auth.token
          );

          if (schedData?.linked) {
            setDurationEditable(
              false,
              'From the linked ACC schedule activity — update the duration in the ACC schedule.'
            );
          } else {
            setDurationEditable(
              true,
              'No ACC schedule activity is linked, so this duration can be edited here.'
            );
          }

          if (schedData?.linked) {
            const a = schedData.activity;
            scheduleActivityContainer.html = `
              <div class="sched-act-panel">
                <div class="sched-act-header">
                  <i class="fa fa-calendar-check"></i> Schedule Activity
                  <span class="sched-act-chip sched-act-chip-linked">Linked</span>
                </div>
                <div class="sched-act-body">
                  <div class="sched-act-title">${a.fileActivityId || ''} \u2014 ${a.name || ''}</div>
                  <div class="sched-act-wbs">${a.wbsPathText || ''}</div>
                  <div class="sched-act-grid">
                    <div class="sched-act-cell">
                      <div class="sched-act-label">Planned Start</div>
                      <div class="sched-act-value">${formatSchedDate(a.plannedStart)}</div>
                    </div>
                    <div class="sched-act-cell">
                      <div class="sched-act-label">Planned Finish</div>
                      <div class="sched-act-value">${formatSchedDate(a.plannedFinish)}</div>
                    </div>
                    <div class="sched-act-cell">
                      <div class="sched-act-label">Duration</div>
                      <div class="sched-act-value">${a.duration ?? '\u2014'} ${a.durationUnits || 'DAYS'}</div>
                    </div>
                    <div class="sched-act-cell">
                      <div class="sched-act-label">Actual Start</div>
                      <div class="sched-act-value">${a.actualStart ? formatSchedDate(a.actualStart) : '\u2014'}</div>
                    </div>
                  </div>
                </div>
              </div>`;
          } else {
            scheduleActivityContainer.html = `
              <div class="sched-act-panel">
                <div class="sched-act-header">
                  <i class="fa fa-calendar-check"></i> Schedule Activity
                  <span class="sched-act-chip sched-act-chip-none">No Match</span>
                </div>
                <div class="sched-act-body">
                  <div class="sched-act-empty">No schedule activity is linked to this package.</div>
                </div>
              </div>`;
          }
        } catch (err) {
          console.error('Error loading schedule activity:', err);
          setDurationEditable(
            false,
            'Could not check the ACC schedule link — reopen the package to edit duration.'
          );
          scheduleActivityContainer.html =
            '<div class="sched-act-error">Failed to load schedule activity data</div>';
        }
      }

      const scheduleContainer = editor?.widgetMap?.swimLaneSchedule;
      if (!scheduleContainer) return;

      // Show loading state
      scheduleContainer.html =
        '<div class="schedule-panel-loading">Loading schedule...</div>';

      // Helper to refresh schedule panel
      const loadSchedule = async () => {
        try {
          const schedule = await WorkflowAPI.getPackageSchedule(
            taskRecord.id,
            auth.token
          );

          if (!schedule?.swim_lanes || schedule.swim_lanes.length === 0) {
            scheduleContainer.html =
              '<div class="schedule-panel-empty">No schedule data available. Set a Field Start Date to generate schedule.</div>';
            return;
          }

          // Build schedule table HTML
          const formatDate = (dateStr) => {
            if (!dateStr) return '\u2014';
            // Parse YYYY-MM-DD directly to avoid UTC-midnight → local-time day shift
            const parts = dateStr.split('-');
            if (parts.length !== 3) return '\u2014';
            const [year, month, day] = parts;
            if (!year || !month || !day) return '\u2014';
            return `${month}/${day}/${year.slice(-2)}`;
          };

          let tableRows = schedule.swim_lanes
            .map((lane) => {
              const nameLabel = `${lane.is_anchor ? '\u2693 ' : ''}${lane.name}${lane.is_current ? ' (Current)' : ''}`;
              const durationLabel =
                lane.duration_days == null
                  ? '\u2014'
                  : lane.is_override
                    ? `${lane.duration_days} days (Custom)`
                    : `${lane.duration_days} days`;
              const actionBtn = lane.is_override
                ? `<button class="schedule-action-btn schedule-remove-override" data-status-id="${lane.id}" data-package-id="${taskRecord.id}">Remove Override</button>`
                : `<button class="schedule-action-btn schedule-add-override" data-status-id="${lane.id}" data-package-id="${taskRecord.id}" data-duration="${lane.duration_days}">Override</button>`;
              const rowClass = lane.is_current ? 'schedule-row-current' : '';

              return `<tr class="${rowClass}">
                <td>${nameLabel}</td>
                <td>${durationLabel}</td>
                <td>${formatDate(lane.due_date)}</td>
                <td>${actionBtn}</td>
              </tr>`;
            })
            .join('');

          scheduleContainer.html = `
            <div class="schedule-panel">
              <div class="schedule-panel-header">
                <i class="fa fa-calendar-alt"></i> Swim Lane Schedule
              </div>
              <div class="schedule-panel-clarification">
                <div class="schedule-panel-clarification-title">Due Date Calculation (working backward from Field Installation)</div>
                <ul class="schedule-panel-clarification-list">
                  <li>Last swim lane before Field Installation: Field start date − 1 day</li>
                  <li>All others: Next swim lane's due date − Next swim lane's duration</li>
                </ul>
              </div>
              <table class="schedule-table">
                <thead>
                  <tr>
                    <th>Swim Lane</th>
                    <th>Duration</th>
                    <th>Due Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>${tableRows}</tbody>
              </table>
            </div>`;
        } catch (err) {
          console.error('Error loading schedule:', err);
          scheduleContainer.html =
            '<div class="schedule-panel-error">Failed to load schedule</div>';
        }
      };

      // Allow schedule to be refreshed when field start date changes (without closing modal)
      scheduleRefreshRef.current = { taskId: taskRecord.id, loadSchedule };

      // Load the schedule
      await loadSchedule();

      // Attach click handlers via event delegation (once)
      const containerEl = scheduleContainer.element || scheduleContainer.el;
      if (containerEl && !containerEl._scheduleHandlerAttached) {
        containerEl._scheduleHandlerAttached = true;
        containerEl.addEventListener('click', async (e) => {
          const btn = e.target.closest('.schedule-action-btn');
          if (!btn) return;

          const statusId = btn.dataset.statusId;
          const pkgId = btn.dataset.packageId;

          if (btn.classList.contains('schedule-add-override')) {
            const currentDuration = parseInt(btn.dataset.duration, 10) || 5;
            const newDuration = prompt(
              'Enter custom duration (days):',
              String(currentDuration)
            );
            if (newDuration === null || newDuration.trim() === '') return;
            const parsed = parseInt(newDuration, 10);
            if (isNaN(parsed) || parsed < 1) {
              alert('Please enter a valid number of days (\u2265 1)');
              return;
            }
            try {
              btn.textContent = 'Saving...';
              btn.disabled = true;
              const overrideResult = await WorkflowAPI.createDurationOverride(
                pkgId,
                statusId,
                parsed,
                null,
                auth.token
              );
              // Update the Bryntum record's dueDate so the card updates immediately
              if (overrideResult?.swim_lane_due_date) {
                const safeDateStr =
                  String(overrideResult.swim_lane_due_date).split('T')[0] +
                  'T12:00:00';
                taskRecord.dueDate = new Date(safeDateStr);
                taskRecord.cls = getDueUrgencyCls(
                  overrideResult.swim_lane_due_date
                );
              } else {
                taskRecord.dueDate = null;
                taskRecord.cls = '';
              }
              await loadSchedule();
              setSnackbar({
                open: true,
                message: 'Duration override created',
                severity: 'success',
              });
            } catch (err) {
              console.error('Error creating override:', err);
              setSnackbar({
                open: true,
                message: 'Failed to create override',
                severity: 'error',
              });
              btn.textContent = 'Override';
              btn.disabled = false;
            }
          } else if (btn.classList.contains('schedule-remove-override')) {
            try {
              btn.textContent = 'Removing...';
              btn.disabled = true;
              const removeResult = await WorkflowAPI.deleteDurationOverride(
                pkgId,
                statusId,
                auth.token
              );
              // Update the Bryntum record's dueDate so the card updates immediately
              if (removeResult?.swim_lane_due_date) {
                const safeDateStr =
                  String(removeResult.swim_lane_due_date).split('T')[0] +
                  'T12:00:00';
                taskRecord.dueDate = new Date(safeDateStr);
                taskRecord.cls = getDueUrgencyCls(
                  removeResult.swim_lane_due_date
                );
              } else {
                taskRecord.dueDate = null;
                taskRecord.cls = '';
              }
              await loadSchedule();
              setSnackbar({
                open: true,
                message: 'Duration override removed',
                severity: 'success',
              });
            } catch (err) {
              console.error('Error removing override:', err);
              setSnackbar({
                open: true,
                message: 'Failed to remove override',
                severity: 'error',
              });
              btn.textContent = 'Remove Override';
              btn.disabled = false;
            }
          }
        });
      }
    },
    [auth.token, isSuperAdmin]
  );

  // Workflow board configuration
  const workflowBoardProps = useMemo(
    () => ({
      // Performance optimizations for large datasets (700+ items)
      useDomTransition: false, // Disable animations for better performance
      stickyHeaders: true,
      columnDragFeature: true,
      columnField: 'status',
      columns: columns,

      // Auto-sort cards by due date (soonest first, nulls at bottom)
      taskSorterFn: (a, b) => {
        const dateA = a.dueDate;
        const dateB = b.dueDate;
        // Packages without due dates sort to the bottom
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return new Date(dateA) - new Date(dateB);
      },

      // Enable virtualization for large datasets
      virtualScrolling: true,
      rowHeight: 100, // Fixed height helps virtualization performance

      // Disable the "Add Task" button in column toolbars
      features: {
        columnToolbars: {
          topItems: {
            addTask: false,
          },
          bottomItems: {
            addTask: false,
          },
        },
      },

      // Wire up the tasks from packages
      project: {
        tasksData: tasks,
        resourcesData: resources,
        assignmentsData: assignments, // Links tasks to resources
        // Listen for task data changes (e.g., dueDate, notes from task editor)
        taskStore: {
          listeners: {
            // Block invalid field start dates before they hit the record / autosave
            beforeUpdate({ record, changes }) {
              if (isRevertingFieldStartDateRef.current) return;
              if (changes?.fieldStartDate === undefined) return;

              const taskId = record?.id;
              const newRaw = getStoreChangeNewValue(changes.fieldStartDate);
              const formattedFsd = fieldStartDateToYmd(newRaw);

              if (
                formattedFsd &&
                isCalendarDateBeyondMaxFieldStartAhead(formattedFsd)
              ) {
                const board = workflowBoardRef.current?.instance;
                const editor = board?.features?.taskEdit?.editor;
                const snap = fieldStartDateSnapshotRef.current;
                const revertValue = snap.taskId === taskId ? snap.value : null;
                const fsdField = editor?.widgetMap?.fieldStartDate;
                if (fsdField) {
                  fsdField.value = revertValue;
                }
                showFieldStartDateTooFarError(editor);
                return false;
              }

              const todayYmd = formatDateAsYmdLocal(new Date());
              if (
                formattedFsd &&
                !isSuperAdmin &&
                todayYmd &&
                formattedFsd < todayYmd
              ) {
                const board = workflowBoardRef.current?.instance;
                const editor = board?.features?.taskEdit?.editor;
                const snap = fieldStartDateSnapshotRef.current;
                const revertValue = snap.taskId === taskId ? snap.value : null;
                const fsdField = editor?.widgetMap?.fieldStartDate;
                if (fsdField) {
                  fsdField.value = revertValue;
                }
                setSnackbar({
                  open: true,
                  message:
                    'Field installation start date cannot be set to a past date. Only dates from today onwards are allowed.',
                  severity: 'error',
                });
                return false;
              }
            },
            // When task data is updated, save to backend
            update: async ({ record, changes }) => {
              // Status drag/drop already persists data and updates due dates; suppress
              // autosave side effects/snackbars while that request is in-flight.
              if (isUpdatingStatusRef.current) {
                return;
              }

              // A status change reaches this listener from two places:
              //  - drag-and-drop → owned by handleTaskDrop (editor is closed),
              //    so we skip it here to avoid a duplicate API call.
              //  - the editor's Status dropdown → persist instantly so the change
              //    saves the moment it's chosen (autoUpdateRecord commits the
              //    combo to the record live). The on-close handler then no-ops
              //    because kanbanData already reflects the new status, or on
              //    failure because the record was reverted.
              if (changes.status !== undefined) {
                const board = workflowBoardRef.current?.instance;
                const editorOpen =
                  !!board?.features?.taskEdit?.editor?.isVisible;
                if (editorOpen) {
                  await applyPackageStatusChangeRef.current({
                    record,
                    newStatusId: record.status,
                    source: 'editor',
                  });
                }
                // Status-only change is fully handled (drag or editor); there is
                // nothing left for the generic autosave below to do.
                if (Object.keys(changes).length === 1) return;
              }

              // Handle fieldStartDate separately (triggers recalculation)
              if (changes.fieldStartDate !== undefined) {
                if (isRevertingFieldStartDateRef.current) {
                  return;
                }

                const taskId = record?.id;
                if (!taskId) return;

                const newRaw = getStoreChangeNewValue(changes.fieldStartDate);
                const formattedFsd = fieldStartDateToYmd(newRaw);

                if (
                  formattedFsd &&
                  isCalendarDateBeyondMaxFieldStartAhead(formattedFsd)
                ) {
                  const board = workflowBoardRef.current?.instance;
                  const editor = board?.features?.taskEdit?.editor;
                  revertFieldStartDateToSnapshot(record, taskId, editor);
                  showFieldStartDateTooFarError(editor);
                  return;
                }

                const todayYmd = formatDateAsYmdLocal(new Date());
                if (
                  formattedFsd &&
                  !isSuperAdmin &&
                  todayYmd &&
                  formattedFsd < todayYmd
                ) {
                  const board = workflowBoardRef.current?.instance;
                  const editor = board?.features?.taskEdit?.editor;
                  revertFieldStartDateToSnapshot(record, taskId, editor);
                  setSnackbar({
                    open: true,
                    message:
                      'Field installation start date cannot be set to a past date. Only dates from today onwards are allowed.',
                    severity: 'error',
                  });
                  return;
                }
                try {
                  const result = await WorkflowAPI.updateFieldStartDate(
                    taskId,
                    formattedFsd,
                    auth.token
                  );
                  fieldStartDateSnapshotRef.current = {
                    taskId,
                    value: record.fieldStartDate ?? null,
                  };
                  const boardAfterSave = workflowBoardRef.current?.instance;
                  const editorAfterSave =
                    boardAfterSave?.features?.taskEdit?.editor;
                  editorAfterSave?.widgetMap?.fieldStartDate?.clearError?.();
                  // Update the record's due date with recalculated value
                  // Append T12:00:00 to avoid timezone-shift off-by-one
                  // (UTC midnight from 'YYYY-MM-DD' → local time shows previous day)
                  if (result?.swim_lane_due_date) {
                    const safeDateStr =
                      String(result.swim_lane_due_date).split('T')[0] +
                      'T12:00:00';
                    record.dueDate = new Date(safeDateStr);
                    // Recalculate urgency border class
                    record.cls = getDueUrgencyCls(result.swim_lane_due_date);
                  } else {
                    record.dueDate = null;
                    record.cls = '';
                  }
                  setSnackbar({
                    open: true,
                    message:
                      'Field start date updated & due dates recalculated',
                    severity: 'success',
                  });
                  // Refresh swim lane schedule in the open editor so user sees new due dates immediately
                  if (
                    scheduleRefreshRef.current &&
                    scheduleRefreshRef.current.taskId === taskId
                  ) {
                    scheduleRefreshRef.current.loadSchedule();
                  }
                } catch (err) {
                  console.error('Error updating field start date:', err);
                  const apiMsg =
                    err.response?.data?.message ||
                    err.response?.data?.error?.message;
                  setSnackbar({
                    open: true,
                    message: apiMsg || 'Failed to update field start date',
                    severity: 'error',
                  });
                }
                // Don't fall through to the general save
                if (
                  Object.keys(changes).length === 1 ||
                  (Object.keys(changes).length === 2 &&
                    changes.dueDate !== undefined)
                ) {
                  return;
                }
              }

              // Check if any saveable field was changed
              const saveableFields = [
                'dueDate',
                'notes',
                'subcontractorRequired',
                'vdcRequired',
                'shopRequired',
                'zone',
                'programArea',
                'workTypeName',
                'workTypeCode',
                'iwpId',
                'twpCode',
                'pmEmployeeId',
                'duration',
              ];
              const hasChanges =
                changes &&
                saveableFields.some((field) => changes[field] !== undefined);

              if (!hasChanges) return;

              const taskId = record?.id;
              if (!taskId) return;

              // Build payload with only the fields that actually changed
              const payload = {};

              // Only include dueDate if it was changed
              if (changes.dueDate !== undefined) {
                const dueDate = record.dueDate;
                if (dueDate) {
                  if (dueDate instanceof Date) {
                    payload.dueDate = dueDate.toISOString().split('T')[0];
                  } else if (typeof dueDate === 'string') {
                    payload.dueDate = dueDate.split('T')[0];
                  }
                } else {
                  payload.dueDate = null; // Explicitly cleared
                }

                // Recalculate urgency border class immediately
                record.cls = getDueUrgencyCls(dueDate);
              }

              // Only include notes if it was changed
              if (changes.notes !== undefined) {
                payload.notes = record.notes || null;
              }

              // Map dropdown string values to booleans for requirement fields
              if (changes.subcontractorRequired !== undefined) {
                payload.subcontractorRequired =
                  record.subcontractorRequired === 'Required';
              }
              if (changes.vdcRequired !== undefined) {
                payload.vdcRequired = record.vdcRequired === 'Required';
              }
              if (changes.shopRequired !== undefined) {
                payload.shopRequired = record.shopRequired === 'Required';
              }

              // ACC custom attribute text fields. These save locally and sync to ACC when a mapping exists.
              if (changes.zone !== undefined) {
                payload.zone = record.zone || null;
              }
              if (changes.programArea !== undefined) {
                payload.programArea = record.programArea || null;
              }
              if (changes.workTypeName !== undefined) {
                payload.workTypeName = record.workTypeName || null;
              }
              if (changes.workTypeCode !== undefined) {
                payload.workTypeCode = record.workTypeCode || null;
              }
              if (changes.iwpId !== undefined) {
                payload.iwpId = record.iwpId || null;
              }
              if (changes.twpCode !== undefined) {
                payload.twpCode = record.twpCode || null;
              }

              // PM assignment — send the value directly (null to clear)
              if (changes.pmEmployeeId !== undefined) {
                payload.pmEmployeeId = record.pmEmployeeId || null;
                // Keep swimlanePmId in sync for Phase 3
                record.swimlanePmId = record.pmEmployeeId || 'unassigned';
              }

              // Installation duration (unlinked packages only — the field is read-only
              // when linked and the backend rejects it). Blank clears to null; 0 is valid.
              if (changes.duration !== undefined) {
                const raw = record.duration;
                if (raw == null || raw === '') {
                  payload.duration = null;
                } else {
                  const num = Number(raw);
                  if (!Number.isFinite(num) || num < 0) {
                    // Skip only duration so other changed fields still save
                    setSnackbar({
                      open: true,
                      message:
                        'Installation duration must be a number of days, 0 or more',
                      severity: 'error',
                    });
                  } else {
                    payload.duration = num;
                  }
                }
              }

              if (Object.keys(payload).length === 0) return;

              try {
                const result = await WorkflowAPI.updatePackageDetails(
                  taskId,
                  payload,
                  auth.token
                );
                setSnackbar({
                  open: true,
                  message: 'Package details saved',
                  severity: 'success',
                });
                if (
                  payload.duration !== undefined &&
                  result?.package &&
                  'planned_field_finish_date' in result.package
                ) {
                  const openEditor =
                    workflowBoardRef.current?.instance?.features?.taskEdit
                      ?.editor;
                  const openRecord =
                    openEditor?.record ?? openEditor?.activeRecord;
                  const dates = editorDataRef.current?.dates;
                  if (dates && String(openRecord?.id) === String(taskId)) {
                    dates.plannedFieldFinishDate =
                      result.package.planned_field_finish_date;
                    const plannedActualWidget =
                      openEditor?.widgetMap?.plannedActualDates;
                    if (plannedActualWidget) {
                      plannedActualWidget.html = renderPlannedActualHtml(dates);
                    }
                  }
                }
                if (
                  payload.shopRequired !== undefined &&
                  result?.package &&
                  'planned_shop_start_date' in result.package
                ) {
                  const newDue = result.package.swim_lane_due_date
                    ? String(result.package.swim_lane_due_date).split('T')[0]
                    : null;
                  const currentDue = record.dueDate
                    ? formatDateAsYmdLocal(record.dueDate)
                    : null;
                  if (newDue !== currentDue) {
                    record.dueDate = newDue
                      ? new Date(`${newDue}T12:00:00`)
                      : null;
                    record.cls = newDue ? getDueUrgencyCls(newDue) : '';
                  }

                  const openEditor =
                    workflowBoardRef.current?.instance?.features?.taskEdit
                      ?.editor;
                  const openRecord =
                    openEditor?.record ?? openEditor?.activeRecord;
                  if (String(openRecord?.id) === String(taskId)) {
                    const dates = editorDataRef.current?.dates;
                    if (dates) {
                      dates.plannedShopStartDate =
                        result.package.planned_shop_start_date;
                      dates.plannedShopFinishDate =
                        result.package.planned_shop_finish_date;
                      dates.plannedFieldFinishDate =
                        result.package.planned_field_finish_date;
                      const plannedActualWidget =
                        openEditor?.widgetMap?.plannedActualDates;
                      if (plannedActualWidget) {
                        plannedActualWidget.html =
                          renderPlannedActualHtml(dates);
                      }
                    }
                    // Swim lane table is computed on read — reload it
                    if (scheduleRefreshRef.current?.taskId === taskId) {
                      scheduleRefreshRef.current.loadSchedule();
                    }
                  }
                }
                if (result?.accSyncFailed) {
                  setAutodeskSyncAuthExpired(!!result.accAuthExpired);
                  setAutodeskSyncWarningOpen(true);
                }
              } catch (err) {
                console.error('Error saving package details:', err);
                const apiMsg =
                  err.response?.data?.message ||
                  err.response?.data?.error?.message;
                setSnackbar({
                  open: true,
                  message: apiMsg || 'Failed to save package details',
                  severity: 'error',
                });
              }
            },
          },
        },
        // Listen for assignment changes to save to backend
        assignmentStore: {
          listeners: {
            // When assignments change, save to backend
            change: async ({ action, records }) => {
              // Skip initial data loading and batch operations
              if (action === 'dataset' || action === 'batch') return;

              // Skip if we're updating from API response (prevents duplicate saves)
              if (isUpdatingFromApiRef.current) {
                console.log('Assignment change skipped - updating from API', {
                  action,
                  recordCount: records?.length,
                });
                return;
              }

              // Only save on add/remove from user interaction
              if (action === 'add' || action === 'remove') {
                console.log('Assignment change from user interaction', {
                  action,
                  recordCount: records?.length,
                });
                const taskIds = new Set(
                  records.map((r) => r.eventId || r.event?.id)
                );

                for (const taskId of taskIds) {
                  if (!taskId) continue;

                  const taskAssignments =
                    workflowBoardRef.current?.instance?.project?.assignmentStore?.query(
                      (a) => (a.eventId || a.event?.id) === taskId
                    ) || [];

                  const resourceIds = taskAssignments
                    .map((a) => a.resourceId || a.resource?.id)
                    .filter(Boolean);

                  try {
                    await WorkflowAPI.savePackageAssignments(
                      taskId,
                      resourceIds,
                      auth.token
                    );
                    const board = workflowBoardRef.current?.instance;
                    const taskStore = board?.project?.taskStore;
                    const taskRecord = taskStore?.allRecords.find(
                      (t) => String(t.id) === String(taskId)
                    );
                    if (taskRecord) {
                      taskRecord.resourceIds = [...resourceIds];
                      const primaryId = resourceIds[0] || null;
                      taskRecord.primaryResourceId = primaryId;
                      taskRecord.swimlaneResourceId = primaryId || 'unassigned';
                      if (primaryId) {
                        const resRec =
                          board.project?.resourceStore?.allRecords.find(
                            (r) => String(r.id) === String(primaryId)
                          );
                        taskRecord.primaryResourceName =
                          resRec?.name || primaryId;
                      } else {
                        taskRecord.primaryResourceName = null;
                      }
                    }
                  } catch (err) {
                    console.error('Error saving assignment:', err);
                  }
                }
              }
            },
          },
        },
      },
      taskRenderer({ taskRecord, cardConfig }) {
        if (taskRecord.archived) {
          if (cardConfig.class && typeof cardConfig.class.add === 'function') {
            cardConfig.class.add('package-card-archived');
          } else if (typeof cardConfig.class === 'object') {
            cardConfig.class['package-card-archived'] = true;
          } else {
            cardConfig.class =
              (cardConfig.class || '') + ' package-card-archived';
          }

          cardConfig.children.header.children.archivedBadge = {
            tag: 'span',
            class: 'package-archived-badge',
            text: 'ARCHIVED',
          };
        }

        // Add notes indicator icon to card header when notes exist
        if (taskRecord.notes && taskRecord.notes.trim().length > 0) {
          cardConfig.children.header.children.notesIcon = {
            tag: 'i',
            class: 'fa fa-sticky-note notes-indicator',
          };
        }

        // Add VDC Design indicator icon to card footer
        if (taskRecord.vdcRequired === 'Required') {
          cardConfig.children.footer.children.vdcIcon = {
            tag: 'i',
            class: 'fa fa-drafting-compass requirement-indicator vdc-indicator',
            title: 'VDC Design Required',
          };
        }

        // Add Prefab/Shop Fabrication indicator icon to card footer
        if (taskRecord.shopRequired === 'Required') {
          cardConfig.children.footer.children.prefabIcon = {
            tag: 'i',
            class: 'fa fa-cart-flatbed requirement-indicator prefab-indicator',
            title: 'Prefab/Shop Fabrication Required',
          };
        }
      },
      footerItems: {
        department: { type: 'text' },
        resourceAvatars: { type: 'resourceAvatars', overlap: true },
        dueDate: {
          type: 'template',
          template: ({ taskRecord }) => {
            if (!taskRecord.dueDate) return '';
            const date =
              taskRecord.dueDate instanceof Date
                ? taskRecord.dueDate
                : new Date(taskRecord.dueDate);
            if (isNaN(date.getTime())) return '';
            const formatted = `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
            return {
              tag: 'span',
              class: 'package-due-date',
              text: formatted,
            };
          },
        },
      },
      tbar: [
        {
          label: 'Filter Name',
          type: 'taskfilterfield',
          placeholder: 'Filter by area...',
          field: 'name',
          filterDelay: 300,
        },
        {
          label: 'Filter Description',
          type: 'taskfilterfield',
          placeholder: 'Filter by package name...',
          field: 'description',
          filterDelay: 300,
        },
      ],
      chainFilters: true,

      listeners: {
        taskEditHide: handleTaskEditHide,
      },
    }),
    [
      columns,
      tasks,
      resources,
      assignments,
      auth.token,
      isSuperAdmin,
      projectTeamMembers,
      showFieldStartDateTooFarError,
      revertFieldStartDateToSnapshot,
      handleTaskEditHide,
    ]
  );

  const totalOnBoard = statusStepSets.reduce(
    (sum, s) => sum + parseInt(s.package_count || 0, 10),
    0
  );
  const totalProject = Number(kanbanData?.totalProjectPackageCount);
  const showPackageCountMismatch =
    !Number.isNaN(totalProject) &&
    totalProject > 0 &&
    statusStepSets.length > 0 &&
    totalOnBoard !== totalProject;

  const swimlaneFieldName = groupByPm
    ? 'swimlanePmId'
    : groupByResource
      ? 'swimlaneResourceId'
      : null;
  const swimlanesData = groupByPm
    ? pmSwimlanesData
    : groupByResource
      ? resourceSwimlanesData
      : null;

  if (!selectedProjectId) {
    return <ProjectEmptyState />;
  }

  return (
    <div
      className={`package-workflow-container${groupByPm || groupByResource ? ' swimlane-active' : ''}`}
    >
      {/* Project Selection Header */}
      <Box
        sx={{
          py: 1,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        {isLoadingKanban && (
          <>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {isSwitchingWorkflow
                ? 'Switching workflow...'
                : 'Loading board data...'}
            </Typography>
          </>
        )}

        {/* Status Step Set (Workflow) Chips */}
        {selectedProjectId && statusStepSets.length > 0 && (
          <>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', ml: 1 }}
            >
              Workflows:
            </Typography>
            {statusStepSets.map((set) => (
              <Chip
                key={set.id}
                label={`${set.name} (${set.package_count})${set.belongs_to_this_project === false ? ' — Other project' : ''}`}
                title={
                  set.belongs_to_this_project === false
                    ? 'This workflow is tied to another Autodesk project. Packages here were assigned to it; contact an administrator to correct.'
                    : undefined
                }
                onClick={() => handleStatusStepSetChange(set.id)}
                color={
                  set.id === selectedStatusStepSetId
                    ? 'primary'
                    : set.id === 'unassigned'
                      ? 'warning'
                      : 'default'
                }
                variant={
                  set.id === selectedStatusStepSetId ? 'filled' : 'outlined'
                }
                clickable
                size="small"
                disabled={isLoadingKanban}
                sx={
                  set.belongs_to_this_project === false
                    ? { borderStyle: 'dashed' }
                    : undefined
                }
              />
            ))}
          </>
        )}

        {/* Right side: filters + Project Team + package count */}
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {/* Group by PM toggle button */}
          {selectedProjectId && kanbanData && (
            <Button
              variant={groupByPm ? 'contained' : 'outlined'}
              color={groupByPm ? 'primary' : 'inherit'}
              startIcon={<ViewStreamIcon />}
              onClick={handleGroupByPmToggle}
              size="small"
              disabled={isLoadingKanban}
              title="Group by PM"
            >
              {isSmallScreen
                ? groupByPm
                  ? 'PM: On'
                  : 'PM'
                : groupByPm
                  ? 'Group by PM: On'
                  : 'Group by PM'}
            </Button>
          )}

          {/* PM Filter Autocomplete */}
          {selectedProjectId && kanbanData && projectTeamMembers.length > 0 && (
            <Autocomplete
              size="small"
              sx={{ minWidth: isSmallScreen ? 140 : 220 }}
              options={pmFilterOptions}
              getOptionLabel={(option) => option.name}
              value={selectedPmFilter}
              onChange={(event, newValue) => handlePmFilterChange(newValue)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              disabled={groupByPm}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={isSmallScreen ? 'PM' : 'Filter by PM'}
                  placeholder="All PMs"
                />
              )}
            />
          )}

          {/* Group by Resource + resource filter (mirror PM) */}
          {selectedProjectId && kanbanData && (
            <Button
              variant={groupByResource ? 'contained' : 'outlined'}
              color={groupByResource ? 'primary' : 'inherit'}
              startIcon={<PeopleOutlineIcon />}
              onClick={handleGroupByResourceToggle}
              size="small"
              disabled={isLoadingKanban}
              title="Group by Resource"
            >
              {isSmallScreen
                ? groupByResource
                  ? 'Res: On'
                  : 'Res'
                : groupByResource
                  ? 'Group by Resource: On'
                  : 'Group by Resource'}
            </Button>
          )}

          {selectedProjectId &&
            kanbanData &&
            boardResourceMembers.length > 0 && (
              <Autocomplete
                size="small"
                sx={{ minWidth: isSmallScreen ? 140 : 220 }}
                options={boardResourceMembers}
                getOptionLabel={(option) => option.name}
                value={selectedResourceFilter}
                onChange={(event, newValue) =>
                  handleResourceFilterChange(newValue)
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={groupByResource}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={isSmallScreen ? 'Resource' : 'Filter by Resource'}
                    placeholder="All resources"
                  />
                )}
              />
            )}

          {/* VDC and Prefab filter chips - shorter labels on small screens */}
          {selectedProjectId && kanbanData && (
            <>
              <Chip
                label={isSmallScreen ? 'VDC' : 'VDC Required'}
                title="VDC Required"
                onClick={handleVdcFilterChange}
                color={vdcFilter ? 'secondary' : 'default'}
                variant={vdcFilter ? 'filled' : 'outlined'}
                size="small"
                clickable
              />
              <Chip
                label={isSmallScreen ? 'Prefab' : 'Prefab Required'}
                title="Prefab Required"
                onClick={handlePrefabFilterChange}
                color={prefabFilter ? 'secondary' : 'default'}
                variant={prefabFilter ? 'filled' : 'outlined'}
                size="small"
                clickable
              />
            </>
          )}

          {/* Package count - between Group by PM and Project Team */}
          {kanbanData && (
            <Typography variant="body2" color="text.secondary">
              {anyFilterActive
                ? `${filteredCount} / ${kanbanData.packages.length} packages`
                : `${kanbanData.packages.length} packages`}
            </Typography>
          )}

          {/* Project Team Button - all the way right */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Package status download (#245) — just before Project Team */}
            {selectedProjectId && selectedStatusStepSetId && (
              <Tooltip title="Download package status" describeChild>
                {/* span keeps the tooltip working while the button is disabled */}
                <span>
                  <IconButton
                    aria-label="Download package status"
                    onClick={() => setExportDialogOpen(true)}
                    disabled={isLoadingKanban}
                    sx={(theme) => ({
                      width: 36,
                      height: 36,
                      border: `1px solid ${theme.palette.ft.neutrals.fieldBorder}`,
                      borderRadius: `${theme.palette.ft.radius.control}px`,
                      color: theme.palette.ft.neutrals.textBody,
                    })}
                  >
                    <FileDownloadOutlined sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {selectedProjectId && selectedStatusStepSetId && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<GroupIcon />}
                onClick={() => setProjectTeamModalOpen(true)}
                size="small"
                disabled={isLoadingKanban}
              >
                Project Team
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {/* Status Messages */}
      {error && (
        <Box sx={{ p: 2 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      {/* Package count mismatch: not all project packages are on the board */}
      {showPackageCountMismatch && (
        <Box sx={{ p: 2 }}>
          <Alert severity="warning">
            Not all packages for the project are showing on the workflow board.
            Please contact an administrator.
          </Alert>
        </Box>
      )}

      {/* Empty State 2: Project selected but no status step sets with packages */}
      {selectedProjectId && kanbanData && statusStepSets.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography color="text.secondary">
            This project has no packages assigned to any workflow. Packages must
            be synced from Autodesk Construction Cloud.
          </Typography>
        </Box>
      )}

      {/* Empty State 3: Status step set selected but no packages */}
      {selectedProjectId &&
        kanbanData &&
        statusStepSets.length > 0 &&
        kanbanData.packages.length === 0 &&
        kanbanData.statuses.length > 0 && (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary">
              No packages found in the "
              {statusStepSets.find((s) => s.id === selectedStatusStepSetId)
                ?.name || 'selected'}
              " workflow
            </Typography>
          </Box>
        )}

      {/* Empty State 4: Status step set selected but no statuses */}
      {selectedProjectId &&
        kanbanData &&
        statusStepSets.length > 0 &&
        kanbanData.statuses.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography color="text.secondary">
              The "
              {statusStepSets.find((s) => s.id === selectedStatusStepSetId)
                ?.name || 'selected'}
              " workflow has no statuses configured
            </Typography>
          </Box>
        )}

      {/* Bryntum TaskBoard - only show when we have statuses */}
      {kanbanData && kanbanData.statuses.length > 0 && (
        <>
          {fieldStartPastWarning && isSuperAdmin && (
            <Alert severity="warning" sx={{ mb: 1, mx: 0.5 }}>
              Setting a past start date may cause prior week planned hours to be
              inaccurate. A snapshot regeneration may be needed after saving.
            </Alert>
          )}
          <BryntumTaskBoard
            key={`workflow-${selectedProjectId}-${selectedStatusStepSetId || 'default'}-${boardRefreshKey}`}
            ref={workflowBoardRef}
            {...workflowBoardProps}
            swimlaneField={swimlaneFieldName}
            swimlanes={swimlanesData}
            taskTooltipFeature={{
              tooltip: {
                forSelector: '.notes-indicator',
                listeners: {
                  beforeShow({ source }) {
                    // Resolve the task record from the hovered card element
                    const taskBoard = workflowBoardRef.current?.instance;
                    if (taskBoard) {
                      const taskRecord = taskBoard.resolveTaskRecord(
                        source.activeTarget
                      );
                      if (
                        !taskRecord?.notes ||
                        taskRecord.notes.trim().length === 0
                      ) {
                        return false; // Cancel tooltip entirely for cards without notes
                      }
                    }
                  },
                },
              },
              template({ taskRecord }) {
                // Escape HTML to prevent XSS
                const escapedNotes = (taskRecord.notes || '')
                  .replace(/&/g, '&amp;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                  .replace(/"/g, '&quot;');
                return `
                <div class="package-notes-tooltip">
                  <div class="package-notes-tooltip-header">
                    <i class="fa fa-sticky-note"></i> Notes
                  </div>
                  <div class="package-notes-tooltip-body">${escapedNotes}</div>
                </div>
              `;
              },
            }}
            taskEditFeature={{
              editorConfig: {
                title: 'Edit Package',
                width: 1100,
                // Fixed height so the modal stays the same size across all tabs;
                // tall tabs (Info/Schedule) scroll internally. Capped to 94vh in CSS
                // for small screens.
                height: 900,
                // NOTE: Save/Cancel footer intentionally omitted for now. The editor
                // runs with autoUpdateRecord:true (Bryntum default) — a live-autosave
                // model the existing save paths depend on (taskStore update listeners,
                // status column moves, handleTaskEditHide). Rendering the bbar requires
                // autoUpdateRecord:false, which rewires those paths; deferred to a
                // dedicated explicit-save phase. See ticket #104 implementation doc §4.3.
              },
              items: {
                name: false, // Hide combined title in modal
                color: false,
                // Suppress Bryntum's built-in `description` field at root — the real
                // "Work Package" description now lives inside the Info tab (Phase 6).
                // Without this, the default description field reappears at root.
                description: false,
                // Suppress the default root-level Status (column) and Assigned To
                // (resources) fields — they now live inside `headerStrip` below.
                // Without this they duplicate at the bottom of the editor.
                column: false,
                resources: false,
                // ── Header strip: Status + Resources stay at ROOT level (never nested
                // in a tab) so their native Bryntum bindings stay intact. Weight
                // overrides position them above the tab panel. (Ticket #104, Phase 4)
                // NOTE: the status dropdown is Bryntum's `column` field (columncombo).
                // The board maps status via columnField:'status'. Because this combo is
                // nested inside a container (headerStrip), it does NOT inherit the native
                // column binding, so it MUST declare name:'status' explicitly — otherwise
                // picking a status never writes back to record.status and the card won't
                // move lanes (the live taskStore `update` listener + handleTaskEditHide
                // both key off record.status). Do not remove that name.
                // Header strip: Status + Assigned To side by side via a NATIVE Bryntum
                // hbox container (CSS flex fights the box-layout engine). The special
                // column/resources fields keep their native types so bindings stay intact.
                headerStrip: {
                  type: 'container',
                  weight: 5,
                  cls: 'editor-header-strip',
                  layout: 'hbox',
                  items: {
                    // Package name + ref block (populated in handleBeforeTaskEditShow)
                    packageInfo: {
                      type: 'widget',
                      cls: 'header-package-container',
                      flex: 1.3,
                      html: '',
                    },
                    column: {
                      type: 'columncombo',
                      label: 'Status',
                      name: 'status',
                      excludeCatchAll: true,
                      flex: 1,
                      // Force includeAllColumnValues:false so the Status combo chains the
                      // board's columns store directly, preserving the workflow order
                      // (sequencenumber) used by the swim lanes. The default (true)
                      // populates via populateDistinctValues and sorts alphabetically.
                      // (Ticket #098 — fix status dropdown order)
                      includeAllColumnValues: false,
                    },
                    resources: {
                      type: 'resourcescombo',
                      label: 'Assigned To',
                      flex: 1,
                      chipView: {
                        itemTpl: (record) => {
                          const name =
                            record?.name || record?.resource?.name || '';
                          return escapeHtml(name);
                        },
                        getItemStyle: () => ({
                          background: '#f1f3f5',
                          backgroundColor: '#f1f3f5',
                          border: '1px solid #dee2e6',
                          color: '#495057',
                        }),
                      },
                    },
                  },
                },
                // ── Four-tab shell (Ticket #104, Phase 5). Content is migrated into
                // these tabs in Phases 6–9; placeholders for now. The existing flat
                // fields below remain at root until their content moves into a tab.
                mainTabs: {
                  type: 'tabpanel',
                  weight: 30,
                  cls: 'package-editor-tabs',
                  flex: 1,
                  items: {
                    infoTab: {
                      title: 'Info',
                      cls: 'package-editor-tab info-tab',
                      layout: 'vbox',
                      items: {
                        // Two columns: editable workflow fields (left) | read-only ACC identity (right)
                        infoColumns: {
                          type: 'container',
                          cls: 'info-columns',
                          layout: 'hbox',
                          items: {
                            infoLeft: {
                              type: 'container',
                              cls: 'info-col info-col-left',
                              layout: 'vbox',
                              flex: 1,
                              defaults: { labelPosition: 'above' },
                              items: {
                                projectManager: {
                                  type: 'combo',
                                  name: 'pmEmployeeId',
                                  label: 'PM',
                                  editable: false,
                                  clearable: true,
                                  placeholder: 'Unassigned',
                                  items: projectTeamMembers.map((m) => ({
                                    value: m.id,
                                    text: m.name,
                                  })),
                                },
                                // VDC / Shop / Subcontractor toggle pills (populated
                                // and wired in handleBeforeTaskEditShow).
                                flagPills: {
                                  type: 'widget',
                                  cls: 'flag-pills-container',
                                  html: '',
                                },
                                trade: {
                                  type: 'textfield',
                                  name: 'trade',
                                  label: 'Trade',
                                  readOnly: true,
                                  cls: 'acc-field',
                                },
                                areaName: {
                                  type: 'textfield',
                                  name: 'areaName',
                                  label: 'Area',
                                  readOnly: true,
                                  cls: 'acc-field',
                                },
                                programArea: {
                                  type: 'textfield',
                                  name: 'programArea',
                                  label: 'Program Area',
                                  cls: 'acc-field acc-editable-field',
                                },
                              },
                            },
                            infoRight: {
                              type: 'container',
                              cls: 'info-col info-col-right',
                              layout: 'vbox',
                              flex: 1,
                              defaults: { labelPosition: 'above' },
                              items: {
                                zone: {
                                  type: 'textfield',
                                  name: 'zone',
                                  label: 'Zone',
                                  cls: 'acc-field acc-editable-field',
                                },
                                workTypeName: {
                                  type: 'textfield',
                                  name: 'workTypeName',
                                  label: 'Work Type',
                                  cls: 'acc-field acc-editable-field',
                                },
                                iwpId: {
                                  type: 'textfield',
                                  name: 'iwpId',
                                  label: 'IWP ID',
                                  cls: 'acc-field acc-editable-field',
                                },
                                twpCode: {
                                  type: 'textfield',
                                  name: 'twpCode',
                                  label: 'TWP Code',
                                  cls: 'acc-field acc-editable-field',
                                },
                                workTypeCode: {
                                  type: 'textfield',
                                  name: 'workTypeCode',
                                  label: 'Work Type Code',
                                  cls: 'acc-field acc-editable-field',
                                },
                              },
                            },
                          },
                        },
                        // Populated in handleBeforeTaskEditShow from editorData.statusHistory
                        trackingStatusHistory: {
                          type: 'widget',
                          cls: 'tracking-status-history-container',
                          html: '',
                        },
                      },
                    },
                    scheduleTab: {
                      title: 'Schedule',
                      cls: 'package-editor-tab schedule-tab',
                      layout: 'vbox',
                      items: {
                        // Section 1 (one row): start date, its lock, duration, due date.
                        // The three inputs flex equally to fill the row; the lock is
                        // content-sized and pulled in tight against the start date.
                        fieldStartRow: {
                          type: 'container',
                          cls: 'schedule-field-start-row',
                          layout: 'hbox',
                          defaults: { labelPosition: 'above' },
                          items: {
                            fieldStartDate: {
                              type: 'datefield',
                              name: 'fieldStartDate',
                              label: 'Field Installation Start Date',
                              labelPosition: 'above',
                              flex: 1,
                              picker: {
                                isDisabledDate: (date) => {
                                  const day = date.getDay(); // 0=Sun, 6=Sat
                                  if (day === 0 || day === 6) return true;
                                  const ymd = parseCalendarYmd(date);
                                  const maxYmd =
                                    getMaxFieldStartDateCalendarYmd();
                                  if (ymd && maxYmd && ymd > maxYmd)
                                    return true;
                                  if (!isSuperAdmin) {
                                    const today = formatDateAsYmdLocal(
                                      new Date()
                                    );
                                    if (today && ymd && ymd < today)
                                      return true;
                                  }
                                  return false;
                                },
                              },
                              listeners: {
                                change({ source, value }) {
                                  const v = value ?? source?.value;
                                  const ymd = fieldStartDateToYmd(v);

                                  if (
                                    ymd &&
                                    isCalendarDateBeyondMaxFieldStartAhead(ymd)
                                  ) {
                                    const board =
                                      workflowBoardRef.current?.instance;
                                    const editor =
                                      board?.features?.taskEdit?.editor;
                                    const taskRecord =
                                      editor?.record ?? editor?.activeRecord;
                                    if (taskRecord?.id) {
                                      revertFieldStartDateToSnapshot(
                                        taskRecord,
                                        taskRecord.id,
                                        editor
                                      );
                                    } else {
                                      const snap =
                                        fieldStartDateSnapshotRef.current;
                                      isRevertingFieldStartDateRef.current = true;
                                      try {
                                        source.value = snap.value ?? null;
                                      } finally {
                                        isRevertingFieldStartDateRef.current = false;
                                      }
                                    }
                                    showFieldStartDateTooFarError(editor);
                                    return;
                                  }
                                  source?.clearError?.();

                                  if (!isSuperAdmin) {
                                    setFieldStartPastWarning(false);
                                    return;
                                  }
                                  if (!v) {
                                    setFieldStartPastWarning(false);
                                    return;
                                  }
                                  const today = formatDateAsYmdLocal(
                                    new Date()
                                  );
                                  const snapYmd = fieldStartDateToYmd(
                                    fieldStartDateSnapshotRef.current.value
                                  );
                                  const changedToPast =
                                    ymd &&
                                    today &&
                                    ymd < today &&
                                    ymd !== snapYmd;
                                  setFieldStartPastWarning(!!changedToPast);
                                },
                              },
                            },
                            // Field-start-date lock + source label (populated and
                            // wired in handleBeforeTaskEditShow)
                            fieldStartLock: {
                              type: 'widget',
                              cls: 'field-start-lock-container',
                              html: '',
                              flex: '0 0 auto',
                            },
                            // Installation duration (packages.duration). Starts read-only;
                            // handleBeforeTaskEditShow unlocks it only when the package has
                            // no ACC schedule link.
                            duration: {
                              type: 'numberfield',
                              name: 'duration',
                              label: 'Installation Duration (Days)',
                              labelPosition: 'above',
                              min: 0,
                              readOnly: true,
                              cls: 'acc-field',
                              flex: 1,
                            },
                            dueDate: {
                              type: 'datefield',
                              name: 'dueDate',
                              label: 'Swim Lane Due Date',
                              labelPosition: 'above',
                              readOnly: true,
                              cls: 'acc-field',
                              flex: 1,
                            },
                          },
                        },
                        // Section 2: Swim lane schedule (existing widget — populated in
                        // handleBeforeTaskEditShow; override buttons unchanged)
                        swimLaneSchedule: {
                          type: 'widget',
                          label: false,
                          cls: 'schedule-panel-container',
                          html: '<div class="schedule-panel-loading">Loading schedule...</div>',
                        },
                        // Section 3: Planned vs Actual dates (populated from editorData.dates)
                        plannedActualDates: {
                          type: 'widget',
                          label: false,
                          cls: 'planned-actual-container',
                          html: '',
                        },
                        // Section 4: ACC schedule activity (read-only; populated in
                        // handleBeforeTaskEditShow)
                        scheduleActivityPanel: {
                          type: 'widget',
                          label: false,
                          cls: 'schedule-activity-panel-container',
                          html: '',
                        },
                      },
                    },
                    hoursTab: {
                      title: 'Hours',
                      cls: 'package-editor-tab hours-tab',
                      layout: 'vbox',
                      items: {
                        // Shop + Field stat cards (populated from editorData.hours),
                        // stacked full-width so each card's metrics fit one row
                        hoursCardsRow: {
                          type: 'container',
                          cls: 'hours-cards-row',
                          layout: 'vbox',
                          items: {
                            shopHoursCard: {
                              type: 'widget',
                              cls: 'hours-card-container',
                              html: '',
                            },
                            fieldHoursCard: {
                              type: 'widget',
                              cls: 'hours-card-container',
                              html: '',
                            },
                          },
                        },
                        // Opens WorkPackageProgressModal (handler attached in
                        // handleBeforeTaskEditShow via DOM event delegation)
                        updateProgressBtnWidget: {
                          type: 'widget',
                          cls: 'update-progress-btn-container',
                          html: '<button type="button" class="update-progress-btn"><i class="fa fa-pen"></i> Update work package progress</button>',
                        },
                        // History buttons — open the shared PackageHistoryModal
                        // (wired via DOM delegation in handleBeforeTaskEditShow,
                        // keyed by data-history-tab). Ticket #216.
                        hoursHistoryButtons: {
                          type: 'widget',
                          cls: 'hours-history-container',
                          html: `<div class="hist-section-label">History</div>
                            <div class="hist-btns-row">
                              <button type="button" class="hist-btn-placeholder" data-history-tab="status"><i class="fa fa-history"></i> Status</button>
                              <button type="button" class="hist-btn-placeholder" data-history-tab="timecards"><i class="fa fa-file-alt"></i> Timecards</button>
                              <button type="button" class="hist-btn-placeholder" data-history-tab="earned"><i class="fa fa-clock"></i> Earned hrs</button>
                              <button type="button" class="hist-btn-placeholder" data-history-tab="budget-split"><i class="fa fa-clock"></i> Budget split</button>
                              <button type="button" class="hist-btn-placeholder" data-history-tab="budget-change"><i class="fa fa-dollar-sign"></i> Budget changes</button>
                              <button type="button" class="hist-btn-placeholder" data-history-tab="snapshots"><i class="fa fa-chart-bar"></i> Snapshots</button>
                            </div>`,
                        },
                      },
                    },
                    notesTab: {
                      title: 'Notes',
                      cls: 'package-editor-tab notes-tab',
                      layout: 'vbox',
                      items: {
                        // Notes textarea — binding/save logic unchanged (autosave via
                        // taskStore update listener + handleTaskEditHide)
                        notes: {
                          type: 'textareafield',
                          name: 'notes',
                          label: 'Notes',
                          labelPosition: 'above',
                          cls: 'notes-textarea',
                          flex: 1,
                        },
                        // Documents placeholder — UI only, no backend (future feature)
                        documentsLabel: {
                          type: 'widget',
                          cls: 'documents-section-label-widget',
                          html: '<div class="documents-section-label">Documents</div>',
                        },
                        documentsPlaceholder: {
                          type: 'widget',
                          cls: 'documents-placeholder-container',
                          html: '<div class="documents-placeholder"><i class="fa fa-paperclip"></i><div class="documents-placeholder-title">Attach files to this package</div><div class="documents-placeholder-sub">PDF, images, Office files — coming soon</div></div>',
                        },
                      },
                    },
                  },
                },
                // All editor fields now live inside their tabs (Phases 6–9).
                // Root items hold only: Status (column), Assigned To (resources),
                // and the tab panel.
              },
            }}
            taskMenuFeature={{
              items: {
                removeTask: false,
              },
            }}
            onTaskDrop={handleTaskDrop}
            onBeforeTaskDrop={validateLaneEligibilityDrop}
            onBeforeTaskEditShow={handleBeforeTaskEditShow}
          />
        </>
      )}

      {/* Project Team Modal */}
      <ProjectTeamModal
        open={projectTeamModalOpen}
        onClose={handleCloseProjectTeamModal}
        onBoardRefreshNeeded={refreshBoardData}
        projectId={selectedProjectId}
        statusStepSetId={selectedStatusStepSetId}
        statusStepSetName={selectedStatusStepSet?.name}
        projectNumber={selectedProject?.number}
        projectName={selectedProject?.name}
        authToken={auth.token}
        userId={auth.userId}
      />

      {/* Package status download (#245) */}
      <PackageExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onDownloaded={(fileName) => {
          setExportDialogOpen(false);
          notify.success(`Downloaded ${fileName}`);
        }}
        authToken={auth.token}
        projectId={selectedProjectId}
        projectNumber={selectedProject?.number}
        projectName={selectedProject?.name}
        workflow={{
          id: selectedStatusStepSetId,
          name: selectedStatusStepSet?.name,
        }}
        boardFilters={{
          pm: selectedPmFilter,
          resource: selectedResourceFilter,
          vdcRequired: vdcFilter,
          prefabRequired: prefabFilter,
        }}
      />

      {/* Update Work Package Progress modal — editor closes before this opens */}
      <WorkPackageProgressModal
        open={wpProgressModalOpen}
        pkg={wpProgressPackage}
        isField={true}
        saving={wpProgressSaving}
        error={wpProgressError}
        onClose={handleCloseWpProgress}
        onSave={handleWpProgressSave}
        onBudgetAllocationConfirm={handleWpProgressBudgetConfirm}
      />

      {/* Shared Package History Modal (Ticket #216) — opened from the Hours-tab
          history buttons; stacks on top of the Bryntum task editor */}
      <PackageHistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        packageId={historyModalPackage?.id}
        autoDeskName={historyModalPackage?.autoDeskName}
        packageName={historyModalPackage?.name}
        area={historyModalPackage?.area}
        defaultTab={historyModalDefaultTab}
      />

      <Backdrop
        open={wpProgressLoading}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={
          snackbar.autoHideDuration ||
          (snackbar.severity === 'error' ? 6000 : 3000)
        }
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Loading overlay while a status change persists to the DB and syncs to
          Autodesk. Driven by isUpdatingStatus, which applyPackageStatusChange
          toggles for every status change (drag, dropdown instant-save, and
          editor close). When the ACC safeguard is on the round trip is quick and
          this just flashes; when it's off it stays up for the real ACC sync.
          High zIndex keeps it above the Bryntum editor popup. */}
      <Backdrop
        open={isUpdatingStatus}
        sx={{
          zIndex: 99999,
          color: '#fff',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="body1">
          Updating status and syncing to Autodesk…
        </Typography>
      </Backdrop>

      {/* Autodesk Sync Warning Dialog */}
      <Dialog
        open={autodeskSyncWarningOpen}
        onClose={() => setAutodeskSyncWarningOpen(false)}
        aria-labelledby="autodesk-sync-warning-title"
        aria-describedby="autodesk-sync-warning-description"
        sx={{ zIndex: 2000000 }}
      >
        <DialogTitle id="autodesk-sync-warning-title">
          {autodeskSyncAuthExpired
            ? 'Autodesk Session Expired'
            : 'Autodesk Sync Warning'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="autodesk-sync-warning-description">
            {autodeskSyncAuthExpired
              ? `Your changes were saved locally, but the Autodesk Construction Cloud connection has expired, so they were not synced to ACC. Please reconnect your Autodesk account (Integrations settings) and save again. If an ACC sync runs before this is resolved, these values may be overwritten.`
              : `Your changes were saved, but we were unable to sync them back to
            Autodesk Construction Cloud. If an ACC sync runs before this is
            resolved, these values may be overwritten. Please notify an
            administrator of this failure and indicate the project, package
            name, and field that was updated.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAutodeskSyncWarningOpen(false)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PackageWorkflow;
